import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import styled, { ThemeProvider, createGlobalStyle, keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FaLinkedin, FaGithub, FaEnvelope, FaGraduationCap, FaArrowRight, FaStar, FaCode, FaShieldAlt, FaBrain, FaCog, FaCheck, FaUser, FaCalendarAlt, FaBookOpen } from 'react-icons/fa';
import { FaXTwitter } from 'react-icons/fa6';
import { FiSun, FiMoon, FiMenu, FiX, FiClock, FiAward, FiUsers, FiExternalLink, FiTarget, FiTool, FiSearch, FiTrendingUp, FiBookOpen, FiZap } from 'react-icons/fi';
import { Link } from 'react-scroll';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import MohamedImage from './assets/images/Mohamed.jpg';
import faqs from './data/faqs.json';
import subjects from './data/subjects.json';





const lightTheme = {
  body: '#fafbfc',
  text: '#0a0e1a',
  textLight: '#586073',
  primary: '#0a0e1a',
  secondary: '#3d4458',
  accent: '#5b6bf0',
  accentHover: '#4858d8',
  accentSecondary: '#8b5fe8',
  gradient: 'linear-gradient(135deg, #5b6bf0 0%, #8b5fe8 100%)',
  gradientText: 'linear-gradient(135deg, #5b6bf0 0%, #a570ff 100%)',
  sectionBg1: '#ffffff',
  sectionBg2: '#f5f7fb',
  card: '#ffffff',
  cardHover: '#f5f7fb',
  boxShadow: '0 1px 3px rgba(10, 14, 26, 0.06), 0 1px 2px rgba(10, 14, 26, 0.04)',
  boxShadowHover: '0 20px 60px -10px rgba(91, 107, 240, 0.18), 0 2px 6px rgba(10, 14, 26, 0.04)',
  border: '#e3e6ee',
  borderLight: '#eef0f6',
  hover: '#f5f7fb',
  success: '#00c853',
  warning: '#ff6f00',
  glassBg: 'rgba(255, 255, 255, 0.7)',
  glassBlur: 'blur(10px)'
};

const darkTheme = {
  body: '#0a0e1a',
  text: '#ffffff',
  textLight: '#a8b1cf',
  primary: '#ffffff',
  secondary: '#c5cce0',
  accent: '#7c8eff',
  accentHover: '#9ba8ff',
  accentSecondary: '#a78bfa',
  gradient: 'linear-gradient(135deg, #7c8eff 0%, #a78bfa 100%)',
  gradientText: 'linear-gradient(135deg, #7c8eff 0%, #c4a8ff 100%)',
  sectionBg1: '#0f1320',
  sectionBg2: '#0a0e1a',
  card: '#141828',
  cardHover: '#1c2138',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3), 0 1px 2px rgba(0, 0, 0, 0.15)',
  boxShadowHover: '0 20px 60px -10px rgba(124, 142, 255, 0.25), 0 2px 6px rgba(0, 0, 0, 0.2)',
  border: '#252b40',
  borderLight: '#1c2138',
  hover: '#1c2138',
  success: '#00e676',
  warning: '#ffb74d',
  glassBg: 'rgba(20, 24, 40, 0.7)',
  glassBlur: 'blur(10px)'
};

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-20px);
  }
`;

const GlobalStyles = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');
  
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  :root {
    --font-sans: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
  }

  body {
    font-family: var(--font-sans);
    line-height: 1.6;
    color: ${({ theme }) => theme.text};
    background-color: ${({ theme }) => theme.body};
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    overflow-x: hidden;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  ::selection {
    background: ${({ theme }) => theme.accent};
    color: white;
  }

  a {
    text-decoration: none;
    color: inherit;
  }

  h1, h2, h3, h4, h5, h6 {
    font-weight: 700;
    letter-spacing: -0.02em;
    line-height: 1.2;
    margin-bottom: 1rem;
    color: ${({ theme }) => theme.primary};
  }

  p {
    margin-bottom: 1rem;
    color: ${({ theme }) => theme.text};
    line-height: 1.7;
  }

  input, textarea, button {
    font-family: inherit;
  }

  .gradient-text {
    background: ${({ theme }) => theme.gradientText};
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    color: transparent;
  }

  /* Accessibility: Focus visible styles */
  *:focus-visible {
    outline: 3px solid ${({ theme }) => theme.accent};
    outline-offset: 2px;
    border-radius: 2px;
  }

  *:focus:not(:focus-visible) {
    outline: none;
  }

  /* Accessibility: respect motion preferences globally */
  @media (prefers-reduced-motion: reduce) {
    *,
    *::before,
    *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
      scroll-behavior: auto !important;
    }
  }

  /* Accessibility: Screen reader only utility */
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  /* Accessibility: Skip navigation link */
  .skip-nav {
    position: absolute;
    top: -100px;
    left: 50%;
    transform: translateX(-50%);
    background: ${({ theme }) => theme.accent};
    color: white;
    padding: 0.75rem 1.5rem;
    border-radius: 0 0 8px 8px;
    font-weight: 600;
    font-size: 0.95rem;
    z-index: 10000;
    transition: top 0.2s ease;
    text-decoration: none;

    &:focus {
      top: 0;
      outline: 3px solid ${({ theme }) => theme.accent};
      outline-offset: 2px;
    }
  }

  /* Accessibility: Reduced motion preference */
  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
      scroll-behavior: auto !important;
    }
  }
`;


const HeaderContainer = styled.header`
  position: fixed;
  width: 100%;
  padding: 1rem 2rem;
  background: ${({ theme }) => theme.glassBg};
  backdrop-filter: ${({ theme }) => theme.glassBlur};
  -webkit-backdrop-filter: ${({ theme }) => theme.glassBlur};
  border-bottom: 1px solid ${({ theme }) => theme.borderLight};
  display: flex;
  align-items: center;
  justify-content: space-between;
  z-index: 1000;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &.scrolled {
    padding: 0.75rem 2rem;
    box-shadow: ${({ theme }) => theme.boxShadow};
  }

  .logo {
    font-size: 1.25rem;
    font-weight: 800;
    letter-spacing: -0.03em;
    color: ${({ theme }) => theme.primary};
    transition: all 0.3s ease;
    
    &:hover {
      transform: translateX(5px);
    }
  }

  .navbar {
    display: flex;
    align-items: center;
    gap: 2.5rem;

    a {
      cursor: pointer;
      font-size: 0.95rem;
      font-weight: 500;
      color: ${({ theme }) => theme.textLight};
      transition: all 0.3s ease;
      position: relative;
      padding: 0.5rem 0;

      &::after {
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        width: 0;
        height: 2px;
        background: ${({ theme }) => theme.accent};
        transition: width 0.3s ease;
      }

      &:hover {
        color: ${({ theme }) => theme.primary};
        
        &::after {
          width: 100%;
        }
      }
    }
  }

  .controls {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .theme-toggle {
    cursor: pointer;
    font-size: 1.1rem;
    color: ${({ theme }) => theme.text};
    width: 40px;
    height: 40px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${({ theme }) => theme.card};
    border: 1px solid ${({ theme }) => theme.border};
    transition: all 0.3s ease;

    &:hover {
      transform: scale(1.05);
      background: ${({ theme }) => theme.hover};
      border-color: ${({ theme }) => theme.accent};
    }
  }

  .mobile-menu-icon {
    display: none;
    cursor: pointer;
    font-size: 1.5rem;
    color: ${({ theme }) => theme.text};
    background: none;
    border: none;
    padding: 0.5rem;
    min-width: 44px;
    min-height: 44px;
    align-items: center;
    justify-content: center;
  }

  @media (max-width: 768px) {
    padding: 1rem;

    .navbar {
      display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
      position: absolute;
      top: 100%;
      left: 0;
      width: 100%;
      flex-direction: column;
      gap: 0.5rem;
      background: ${({ theme }) => theme.glassBg};
      backdrop-filter: ${({ theme }) => theme.glassBlur};
      padding: 1.5rem;
      border-bottom: 1px solid ${({ theme }) => theme.border};
      box-shadow: ${({ theme }) => theme.boxShadow};
    }

    .mobile-menu-icon {
      display: flex;
    }
  }
`;

// NavItem: smooth-scrolls within "/" via react-scroll; on subject pages it falls
// back to an anchor link that navigates back to "/" and lets the browser scroll.
function NavItem({ section, onClick, children }) {
  const location = useLocation();
  const isHome = location.pathname === '/';
  if (isHome) {
    return (
      <Link to={section} smooth duration={500} onClick={onClick} tabIndex="0" role="link">
        {children}
      </Link>
    );
  }
  return (
    <a href={`/#${section}`} onClick={onClick} tabIndex="0">
      {children}
    </a>
  );
}

function Header({ toggleTheme, theme }) {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <HeaderContainer isOpen={isOpen} className={scrolled ? 'scrolled' : ''}>
      <motion.div 
        className="logo"
        initial={{ x: -20 }}
        animate={{ x: 0 }}
        transition={{ duration: 0.5 }}
      >
        CS Tutoring
      </motion.div>
      
      <button 
        className="mobile-menu-icon" 
        onClick={() => setIsOpen(!isOpen)}
        aria-label={isOpen ? 'Close navigation menu' : 'Open navigation menu'}
        aria-expanded={isOpen}
        aria-controls="main-navigation"
      >
        {isOpen ? <FiX aria-hidden="true" /> : <FiMenu aria-hidden="true" />}
      </button>

      <nav className="navbar" id="main-navigation" aria-label="Main navigation">
        <NavItem section="home" onClick={() => setIsOpen(false)}>Home</NavItem>
        <NavItem section="testimonials" onClick={() => setIsOpen(false)}>Testimonials</NavItem>
        <NavItem section="how-i-help" onClick={() => setIsOpen(false)}>How I Help</NavItem>
        <NavItem section="subjects" onClick={() => setIsOpen(false)}>Subjects</NavItem>
        <NavItem section="about" onClick={() => setIsOpen(false)}>About</NavItem>
        <NavItem section="faq" onClick={() => setIsOpen(false)}>FAQ</NavItem>
        <NavItem section="contact" onClick={() => setIsOpen(false)}>Get Started</NavItem>
      </nav>

      <div className="controls">
        <motion.button 
          className="theme-toggle" 
          onClick={toggleTheme}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          aria-label={theme === 'light' ? 'Switch to dark theme' : 'Switch to light theme'}
        >
          {theme === 'light' ? <FiMoon aria-hidden="true" /> : <FiSun aria-hidden="true" />}
        </motion.button>
      </div>
    </HeaderContainer>
  );
}


const HeroSection = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  padding: 6rem 2rem 4rem;
  background: ${({ theme }) => theme.body};
  position: relative;
  overflow: hidden;

  /* Ambient mesh-gradient orbs: blue (top-right) + purple (bottom-left).
     Slow drift gives depth without distracting. */
  &::before,
  &::after {
    content: '';
    position: absolute;
    border-radius: 50%;
    filter: blur(80px);
    pointer-events: none;
    will-change: transform;
  }

  &::before {
    top: -20%;
    right: -10%;
    width: 60vw;
    height: 60vw;
    max-width: 800px;
    max-height: 800px;
    background: radial-gradient(circle, ${({ theme }) => theme.accent}33 0%, transparent 65%);
    animation: ${float} 22s ease-in-out infinite;
  }

  &::after {
    bottom: -25%;
    left: -15%;
    width: 55vw;
    height: 55vw;
    max-width: 700px;
    max-height: 700px;
    background: radial-gradient(circle, ${({ theme }) => theme.accentSecondary}26 0%, transparent 65%);
    animation: ${float} 26s ease-in-out infinite reverse;
  }

  @media (prefers-reduced-motion: reduce) {
    &::before,
    &::after {
      animation: none;
    }
  }

  .hero-container {
    max-width: 1200px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: 1fr 350px;
    gap: 4rem;
    align-items: center;
    width: 100%;
    position: relative;
    z-index: 1;
  }

  .hero-content {
    h1 {
      font-size: clamp(2.5rem, 6vw, 4rem);
      font-weight: 800;
      margin-bottom: 1.5rem;
      color: ${({ theme }) => theme.primary};
      line-height: 1.1;
      letter-spacing: -0.03em;
      animation: ${fadeInUp} 0.8s ease-out;
    }

    .subtitle {
      font-size: clamp(1.25rem, 3vw, 1.5rem);
      margin-bottom: 1rem;
      font-weight: 600;
      color: ${({ theme }) => theme.accent};
      animation: ${fadeInUp} 0.8s ease-out 0.1s both;
    }

    .description {
      font-size: clamp(1rem, 2.5vw, 1.125rem);
      color: ${({ theme }) => theme.textLight};
      margin-bottom: 1rem;
      line-height: 1.8;
      animation: ${fadeInUp} 0.8s ease-out 0.2s both;

      &.secondary {
        font-size: clamp(0.9rem, 2vw, 1rem);
        margin-bottom: 2rem;
      }
    }

    .stats {
      display: flex;
      gap: 2rem;
      margin-bottom: 2.5rem;
      animation: ${fadeInUp} 0.8s ease-out 0.3s both;

      .stat {
        text-align: center;

        .number {
          font-size: 2.25rem;
          font-weight: 800;
          letter-spacing: -0.02em;
          background: ${({ theme }) => theme.gradientText};
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          color: transparent;
          display: block;
          line-height: 1.1;
        }

        .label {
          font-size: 0.875rem;
          color: ${({ theme }) => theme.textLight};
          font-weight: 500;
          letter-spacing: 0.01em;
        }
      }
    }

    .trust-pills {
      display: flex;
      flex-wrap: wrap;
      gap: 0.55rem;
      margin: 1rem 0 1.75rem;
      animation: ${fadeInUp} 0.8s ease-out 0.25s both;
    }

    .trust-pill {
      display: inline-flex;
      align-items: center;
      padding: 0.4rem 0.85rem;
      background: ${({ theme }) => theme.card};
      border: 1px solid ${({ theme }) => theme.accent}40;
      border-radius: 50px;
      font-size: 0.82rem;
      font-weight: 600;
      color: ${({ theme }) => theme.text};

      &:first-child {
        background: ${({ theme }) => theme.accent}1A;
        color: ${({ theme }) => theme.accent};
        border-color: ${({ theme }) => theme.accent};
        font-weight: 700;
      }
    }

    .cta-helper {
      font-size: 0.9rem;
      color: ${({ theme }) => theme.textLight};
      margin-top: 0.85rem;
      margin-bottom: 0;
      max-width: 520px;
      line-height: 1.5;
      animation: ${fadeInUp} 0.8s ease-out 0.5s both;
    }

    .cta-group {
      display: flex;
      gap: 1rem;
      flex-wrap: wrap;
      animation: ${fadeInUp} 0.8s ease-out 0.4s both;

      .primary-btn {
        display: inline-flex;
        align-items: center;
        gap: 0.75rem;
        padding: 0.875rem 2rem;
        background: ${({ theme }) => theme.gradient};
        color: white;
        border-radius: 12px;
        font-weight: 600;
        font-size: 1rem;
        transition: transform 0.3s ease, box-shadow 0.3s ease;
        box-shadow: 0 4px 18px -2px ${({ theme }) => theme.accent}40,
                    0 1px 2px rgba(0, 0, 0, 0.1);

        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 30px -4px ${({ theme }) => theme.accent}66,
                      0 2px 4px rgba(0, 0, 0, 0.12);
        }

        &:focus-visible {
          outline: 2px solid ${({ theme }) => theme.accent};
          outline-offset: 3px;
        }

        @media (prefers-reduced-motion: reduce) {
          &:hover { transform: none; }
        }

        svg {
          transition: transform 0.3s ease;
        }

        &:hover svg {
          transform: translateX(3px);
        }
      }

      .secondary-btn {
        display: inline-flex;
        align-items: center;
        gap: 0.75rem;
        padding: 0.875rem 2rem;
        background: transparent;
        color: ${({ theme }) => theme.text};
        border: 2px solid ${({ theme }) => theme.border};
        border-radius: 12px;
        font-weight: 600;
        font-size: 1rem;
        transition: all 0.3s ease;

        &:hover {
          background: ${({ theme }) => theme.card};
          border-color: ${({ theme }) => theme.accent};
          transform: translateY(-2px);
        }
      }
    }
  }

  .hero-image {
    display: flex;
    justify-content: center;
    animation: ${fadeInUp} 1s ease-out 0.3s both;

    .image-wrapper {
      position: relative;
      
      .image-container {
        position: relative;
        width: clamp(250px, 30vw, 320px);
        height: clamp(250px, 30vw, 320px);
        border-radius: 30px;
        overflow: hidden;
        background: ${({ theme }) => theme.gradient};
        box-shadow: 0 20px 60px -10px ${({ theme }) => theme.accent}40;

        &::before {
          content: '';
          position: absolute;
          inset: -2px;
          background: ${({ theme }) => theme.gradient};
          border-radius: 30px;
          z-index: -1;
          opacity: 0.5;
          filter: blur(20px);
        }

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
      }

      .decoration {
        position: absolute;
        width: 100%;
        height: 100%;
        border: 2px solid ${({ theme }) => theme.accent}20;
        border-radius: 30px;
        top: 20px;
        left: 20px;
        z-index: -1;
      }
    }
  }

  @media (max-width: 1024px) {
    .hero-container {
      grid-template-columns: 1fr;
      text-align: center;
      gap: 3rem;
      max-width: 700px;
    }

    .hero-content {
      .stats {
        justify-content: center;
      }

      .cta-group {
        justify-content: center;
      }
    }
  }

  @media (max-width: 768px) {
    padding: 6rem 1rem 3rem;
    min-height: auto;

    .hero-content {
      h1 {
        font-size: clamp(2rem, 8vw, 2.75rem);
        word-break: break-word;
      }

      .description {
        font-size: 0.95rem;
      }

      .stats {
        gap: 1rem;

        .stat .number {
          font-size: 1.5rem;
        }
      }
    }
  }

  @media (max-width: 400px) {
    padding: 5.5rem 0.75rem 2.5rem;

    .hero-content h1 {
      font-size: 1.75rem;
    }

    .hero-content .subtitle {
      font-size: 1.1rem;
    }
  }
`;

function Hero() {
  return (
    <HeroSection id="home">
      <div className="hero-container">
        <div className="hero-content">
          <h1>Advanced <span className="gradient-text">Computer Science</span> Tutoring</h1>
          <div className="subtitle">For Hard CS Courses, Projects, and Exams</div>
          <p className="description">
            Personalized help with hard CS coursework, projects, and exams from a Virginia Tech{' '}
            <span className="gradient-text">CS PhD student</span>,{' '}
            <span className="gradient-text">Graduate Teaching Assistant</span>, and{' '}
            <span className="gradient-text">published ML researcher</span>.
          </p>
          <p className="description">
            Programming, algorithms, AI/ML, cybersecurity, operating systems, computer architecture, AP Computer Science, science-fair mentoring, and CS competition prep.
          </p>

          <div className="trust-pills">
            <span className="trust-pill">$60/hour</span>
            <span className="trust-pill">Free 15-min call</span>
            <span className="trust-pill">Replies within 24 hours</span>
            <span className="trust-pill">Urgent requests welcome</span>
          </div>

          <div className="stats">
            <div className="stat">
              <span className="number">1800+</span>
              <span className="label">Hours Taught</span>
            </div>
            <div className="stat">
              <span className="number">4.0</span>
              <span className="label">Graduate GPA</span>
            </div>
            <div className="stat">
              <span className="number">250+</span>
              <span className="label">5-Star Ratings</span>
            </div>
          </div>

          <div className="cta-group">
            <Link to="contact" smooth duration={500} className="primary-btn" tabIndex="0" role="link">
              Book a Free 15-Min Call <FaArrowRight aria-hidden="true" />
            </Link>
            <Link to="subjects" smooth duration={500} className="secondary-btn" tabIndex="0" role="link">
              See How I Can Help
            </Link>
          </div>
          <p className="cta-helper">
            Tell me your course, assignment, deadline, and what you're stuck on. I'll let you know if I can help.
          </p>
        </div>

        <div className="hero-image">
          <div className="image-wrapper">
            <div className="image-container">
              <img
                src={MohamedImage}
                alt="Mohamed Abdelnaby, Virginia Tech computer science PhD student, graduate teaching assistant, and online tutor specializing in machine learning, AI, cybersecurity, algorithms, and systems"
              />
            </div>
            <div className="decoration" />
          </div>
        </div>
      </div>
    </HeroSection>
  );
}


const TACredibilitySection = styled.section`
  padding: 3rem 2rem;
  background: ${({ theme }) => theme.body};

  .container {
    max-width: 1100px;
    margin: 0 auto;
  }

  .ta-card {
    display: flex;
    gap: 1.5rem;
    align-items: flex-start;
    padding: 1.75rem 2rem;
    background: ${({ theme }) => theme.card};
    border: 1px solid ${({ theme }) => theme.accent}40;
    border-left: 4px solid ${({ theme }) => theme.accent};
    border-radius: 16px;
    box-shadow: ${({ theme }) => theme.boxShadow};
    position: relative;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      top: -50%;
      right: -10%;
      width: 40%;
      height: 200%;
      background: radial-gradient(circle, ${({ theme }) => theme.accent}12 0%, transparent 60%);
      pointer-events: none;
    }
  }

  .ta-icon {
    flex-shrink: 0;
    width: 52px;
    height: 52px;
    border-radius: 12px;
    background: ${({ theme }) => theme.gradient};
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 1.5rem;
    position: relative;
    z-index: 1;
  }

  .ta-content {
    flex: 1;
    position: relative;
    z-index: 1;
  }

  .ta-label {
    display: inline-block;
    font-size: 0.72rem;
    font-weight: 700;
    color: ${({ theme }) => theme.accent};
    text-transform: uppercase;
    letter-spacing: 0.1em;
    margin-bottom: 0.5rem;
  }

  .ta-content p {
    font-size: 1rem;
    line-height: 1.65;
    color: ${({ theme }) => theme.text};
    margin: 0;

    strong {
      color: ${({ theme }) => theme.text};
      font-weight: 700;
    }
  }

  @media (max-width: 640px) {
    padding: 2rem 1rem;
    .ta-card {
      flex-direction: column;
      gap: 1rem;
      padding: 1.5rem;
    }
    .ta-content p { font-size: 0.95rem; }
  }
`;

function TACredibility() {
  return (
    <TACredibilitySection aria-label="University teaching experience">
      <div className="container">
        <div className="ta-card">
          <div className="ta-icon" aria-hidden="true">
            <FaGraduationCap />
          </div>
          <div className="ta-content">
            <span className="ta-label">University Teaching Experience</span>
            <p>
              <strong>Graduate Teaching Assistant</strong> at Virginia Tech for{' '}
              <strong>CS 5704 Software Engineering</strong> and{' '}
              <strong>CS 3754 Cloud Software Engineering</strong>. I held office
              hours, answered student questions, helped debug code, gave project
              feedback, developed grading rubrics, supported autograder development,
              and guided undergraduate and graduate students through DevOps, Docker,
              CI/CD, cloud architecture, and distributed systems.
            </p>
          </div>
        </div>
      </div>
    </TACredibilitySection>
  );
}


const AboutSection = styled.section`
  padding: 5rem 2rem;
  background: ${({ theme }) => theme.sectionBg1};

  .container {
    max-width: 1100px;
    margin: 0 auto;
  }

  h2 {
    font-size: clamp(2.5rem, 5vw, 3rem);
    text-align: center;
    margin-bottom: 4rem;
    font-weight: 800;
    letter-spacing: -0.03em;
  }

  .about-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4rem;
    align-items: start;
  }

  .about-content {
    p {
      font-size: 1.0625rem;
      line-height: 1.8;
      color: ${({ theme }) => theme.textLight};
      margin-bottom: 1.5rem;
      
      &:first-child {
        font-size: 1.125rem;
        font-weight: 500;
        color: ${({ theme }) => theme.text};
      }
    }

    .highlight {
      color: ${({ theme }) => theme.accent};
      font-weight: 600;
    }
  }

  .credentials {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;

    .credential-card {
      background: ${({ theme }) => theme.card};
      padding: 2rem;
      border-radius: 20px;
      border: 1px solid ${({ theme }) => theme.borderLight};
      transition: all 0.3s ease;
      position: relative;
      overflow: hidden;

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 4px;
        background: ${({ theme }) => theme.gradient};
        transform: scaleX(0);
        transition: transform 0.3s ease;
      }

      &:hover {
        transform: translateY(-5px);
        box-shadow: ${({ theme }) => theme.boxShadowHover};
        
        &::before {
          transform: scaleX(1);
        }
      }

      .credential-icon {
        width: 50px;
        height: 50px;
        background: ${({ theme }) => theme.gradient};
        border-radius: 15px;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 1rem;
        
        svg {
          color: white;
          font-size: 1.25rem;
        }
      }

      .credential-title {
        font-size: 1.25rem;
        font-weight: 700;
        color: ${({ theme }) => theme.primary};
        margin-bottom: 0.5rem;
      }

      .credential-subtitle {
        color: ${({ theme }) => theme.accent};
        font-weight: 500;
        margin-bottom: 0.5rem;
      }

      .credential-year {
        color: ${({ theme }) => theme.textLight};
        font-size: 0.875rem;
      }
    }

    a.credential-card {
      text-decoration: none;
      cursor: pointer;

      .credential-year {
        color: ${({ theme }) => theme.accent};
        font-weight: 500;
      }
    }
  }

  .publication-links {
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
    margin: 1rem 0 0.5rem;
  }

  .pub-link {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.5rem 1rem;
    background: ${({ theme }) => theme.accent}15;
    color: ${({ theme }) => theme.accent};
    border: 1px solid ${({ theme }) => theme.accent}40;
    border-radius: 10px;
    font-size: 0.875rem;
    font-weight: 600;
    text-decoration: none;
    transition: all 0.3s ease;

    svg {
      font-size: 0.8rem;
      transition: transform 0.3s ease;
    }

    &:hover {
      background: ${({ theme }) => theme.accent}25;
      border-color: ${({ theme }) => theme.accent};
      transform: translateY(-2px);

      svg {
        transform: translate(2px, -2px);
      }
    }
  }

  @media (max-width: 968px) {
    .about-grid {
      grid-template-columns: 1fr;
      gap: 3rem;
    }
  }

  @media (max-width: 568px) {
    padding: 4rem 1rem;
  }
`;

function About() {
  return (
    <AboutSection id="about">
      <div className="container">
        <motion.h2
          initial={{ y: 30 }}
          whileInView={{ y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          About Your <span className="gradient-text">Tutor</span>
        </motion.h2>
        
        <div className="about-grid">
          <motion.div 
            className="about-content"
            initial={{ x: -30 }}
            whileInView={{ x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <p>
              Hi, I'm <span className="highlight">Mohamed</span>. I tutor CS students who are stuck on a hard course,
              a specific assignment, or a research project. Most of the people I work with are in the middle of a
              tough semester and just need someone who knows the material to sit with them and figure it out.
            </p>
            
            <p>
              I graduated <span className="highlight">Magna Cum Laude</span> with a Bachelor's in Computer Engineering and completed my 
              Master's in Computer Science at the University of Oklahoma with a <span className="highlight">perfect 4.0 GPA</span>. During 
              my graduate studies, I published <strong>two first-author peer-reviewed research papers</strong> in Machine Learning and 
              Computational Biology, one at an international conference (ICCABS 2025, Springer) and one in the Journal of Computational 
              Biology (2026). I'm currently pursuing my PhD in Computer Science at Virginia Tech, where I continue working on advanced topics in machine learning, computing systems, and research.
            </p>
            <div className="publication-links">
              <a href="https://mohamedabdelnaby.com" target="_blank" rel="noopener noreferrer" className="pub-link" aria-label="View my publications on my portfolio website (opens in new tab)">
                View My Publications <FiExternalLink aria-hidden="true" />
              </a>
              <a href="https://scholar.google.com/citations?user=QQTI2EUAAAAJ&hl=en" target="_blank" rel="noopener noreferrer" className="pub-link" aria-label="View my profile on Google Scholar (opens in new tab)">
                Google Scholar <FiExternalLink aria-hidden="true" />
              </a>
            </div>

            <p>
              In addition to private tutoring, I have served as a{' '}
              <span className="highlight">Graduate Teaching Assistant at Virginia Tech</span> for{' '}
              <strong>CS 5704 Software Engineering</strong> and{' '}
              <strong>CS 3754 Cloud Software Engineering</strong>, working directly with
              undergraduate and graduate CS students on debugging, project design, grading rubrics,
              DevOps, Docker, CI/CD, cloud architecture, distributed systems, autograder
              development, and office hours. That experience feeds back into how I help students
              here, focused on writing better, cleaner, and more reliable software, not just
              getting the assignment to compile.
            </p>

            <p>
              Every student is different. Some come in with a specific error message and a deadline tomorrow.
              Others want to actually understand the material before the next exam. I figure out which one you
              are in the first few minutes, then we work the problem together rather than me lecturing at you.
            </p>

            <p>
              I reply quickly, I show up on time, and if a concept has not clicked yet, I slow down,
              try a different explanation, and make sure the student leaves with a clearer path forward.
              That is the job.
            </p>
          </motion.div>

          <motion.div 
            className="credentials"
            initial={{ x: 30 }}
            whileInView={{ x: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="credential-card">
              <div className="credential-icon" aria-hidden="true">
                <FaGraduationCap />
              </div>
              <div className="credential-title">PhD in Computer Science</div>
              <div className="credential-subtitle">Virginia Tech</div>
              <div className="credential-year">2025 - Present</div>
            </div>

            <div className="credential-card">
              <div className="credential-icon" aria-hidden="true">
                <FaGraduationCap />
              </div>
              <div className="credential-title">MS in Computer Science</div>
              <div className="credential-subtitle">University of Oklahoma</div>
              <div className="credential-year">2024 - 2025 | 4.0 GPA</div>
            </div>

            <div className="credential-card">
              <div className="credential-icon" aria-hidden="true">
                <FaGraduationCap />
              </div>
              <div className="credential-title">BS in Computer Engineering</div>
              <div className="credential-subtitle">University of Oklahoma</div>
              <div className="credential-year">2020 - 2024 | Magna Cum Laude</div>
            </div>

          </motion.div>
        </div>
      </div>
    </AboutSection>
  );
}


const CommonReasonsSection = styled.section`
  padding: 5rem 2rem;
  background: ${({ theme }) => theme.sectionBg2};

  .container {
    max-width: 1100px;
    margin: 0 auto;
  }

  h2 {
    font-size: clamp(2rem, 5vw, 3rem);
    text-align: center;
    margin-bottom: 1rem;
    font-weight: 800;
    letter-spacing: -0.03em;

    span {
      color: ${({ theme }) => theme.accent};
    }
  }

  .section-subtitle {
    text-align: center;
    color: ${({ theme }) => theme.textLight};
    font-size: 1.05rem;
    margin: 0 auto 3rem;
    line-height: 1.6;
    max-width: 640px;
  }

  .reasons-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 1.5rem;
  }

  .reason-card {
    padding: 1.75rem 1.5rem;
    background: ${({ theme }) => theme.card};
    border-radius: 18px;
    border: 1px solid ${({ theme }) => theme.borderLight};
    transition: transform 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;

    &:hover {
      transform: translateY(-4px);
      border-color: ${({ theme }) => theme.accent}66;
      box-shadow: ${({ theme }) => theme.boxShadow};
    }

    @media (prefers-reduced-motion: reduce) {
      transition: border-color 0.2s ease;
      &:hover { transform: none; }
    }

    .reason-icon {
      width: 48px;
      height: 48px;
      background: ${({ theme }) => theme.accent}1A;
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;

      svg {
        color: ${({ theme }) => theme.accent};
        font-size: 1.4rem;
      }
    }

    h3 {
      font-size: 1.05rem;
      font-weight: 700;
      color: ${({ theme }) => theme.primary};
      margin: 0;
    }

    p {
      font-size: 0.92rem;
      line-height: 1.6;
      color: ${({ theme }) => theme.textLight};
      margin: 0;
    }
  }

  .pain-search {
    margin-top: 3.5rem;
    padding-top: 2.5rem;
    border-top: 1px solid ${({ theme }) => theme.borderLight};
    text-align: center;
  }

  .pain-search-label {
    display: block;
    font-size: 0.72rem;
    font-weight: 700;
    color: ${({ theme }) => theme.accent};
    text-transform: uppercase;
    letter-spacing: 0.12em;
    margin-bottom: 1.25rem;
  }

  .pain-search-chips {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.6rem;
    max-width: 900px;
    margin: 0 auto;
  }

  .pain-chip {
    padding: 0.5rem 1rem;
    background: ${({ theme }) => theme.card};
    border: 1px solid ${({ theme }) => theme.borderLight};
    border-radius: 50px;
    font-size: 0.88rem;
    color: ${({ theme }) => theme.textLight};
    transition: border-color 0.2s ease, color 0.2s ease;

    &:hover {
      border-color: ${({ theme }) => theme.accent};
      color: ${({ theme }) => theme.text};
    }
  }

  @media (max-width: 768px) {
    padding: 4rem 1rem;
  }
`;

function CommonReasons() {
  const reasons = [
    {
      icon: <FiSearch />,
      title: "Debugging Help",
      body: "Bring your code, error messages, and assignment instructions. We'll work through why it's failing and how to fix it together."
    },
    {
      icon: <FiTarget />,
      title: "Exam Prep",
      body: "Review the topics most likely to appear, walk through practice problems step by step, and build confidence before your midterm or final."
    },
    {
      icon: <FiTool />,
      title: "Project Guidance",
      body: "Break down the requirements, design the solution, plan implementation, and make sure you understand the concepts, not just produce code."
    },
    {
      icon: <FaBrain />,
      title: "Concept Rescue",
      body: "Recursion, pointers, dynamic programming, concurrency, ML math, SQL injection labs, assembly, and other concepts that need a second pass."
    },
  ];

  return (
    <CommonReasonsSection id="how-i-help">
      <div className="container">
        <motion.h2
          initial={{ y: 30 }}
          whileInView={{ y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          Stuck on a CS Assignment, Exam, or <span className="gradient-text">Project?</span>
        </motion.h2>
        <motion.p
          className="section-subtitle"
          initial={{ y: 20 }}
          whileInView={{ y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          viewport={{ once: true }}
        >
          Whether you need help understanding the material or working through a specific problem, here's how sessions usually go.
        </motion.p>
        <motion.div
          className="reasons-grid"
          initial={{ y: 30 }}
          whileInView={{ y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
        >
          {reasons.map((reason, index) => (
            <div className="reason-card" key={index}>
              <div className="reason-icon" aria-hidden="true">
                {reason.icon}
              </div>
              <h3>{reason.title}</h3>
              <p>{reason.body}</p>
            </div>
          ))}
        </motion.div>

        <div className="pain-search">
          <span className="pain-search-label">Students usually come to me when...</span>
          <div className="pain-search-chips">
            <span className="pain-chip">They are falling behind in data structures</span>
            <span className="pain-chip">Recursion finally needs to click</span>
            <span className="pain-chip">A Java, Python, C++, or web project is stuck</span>
            <span className="pain-chip">Operating systems assignments feel overwhelming</span>
            <span className="pain-chip">Machine learning math or code is confusing</span>
            <span className="pain-chip">Cybersecurity labs need a clearer walkthrough</span>
            <span className="pain-chip">Computer architecture or assembly feels too abstract</span>
            <span className="pain-chip">Debugging is taking hours with no progress</span>
            <span className="pain-chip">Pointers, memory, or concurrency need a second pass</span>
            <span className="pain-chip">Dynamic programming needs structure, not memorization</span>
          </div>
        </div>
      </div>
    </CommonReasonsSection>
  );
}

const WhoIHelpSection = styled.section`
  padding: 5rem 2rem;
  background: ${({ theme }) => theme.sectionBg2};

  .container {
    max-width: 1000px;
    margin: 0 auto;
  }

  h2 {
    font-size: clamp(2rem, 5vw, 3rem);
    text-align: center;
    margin-bottom: 3rem;
    font-weight: 800;
    letter-spacing: -0.03em;

    span {
      color: ${({ theme }) => theme.accent};
    }
  }

  .audience-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;
  }

  .audience-card {
    background: ${({ theme }) => theme.card};
    padding: 2rem;
    border-radius: 20px;
    border: 1px solid ${({ theme }) => theme.borderLight};
    transition: all 0.3s ease;
    display: flex;
    gap: 1.25rem;
    align-items: flex-start;

    &:hover {
      transform: translateY(-4px);
      box-shadow: ${({ theme }) => theme.boxShadowHover};
    }

    .audience-icon {
      width: 48px;
      height: 48px;
      min-width: 48px;
      background: ${({ theme }) => theme.gradient};
      border-radius: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
      
      svg {
        color: white;
        font-size: 1.2rem;
      }
    }

    .audience-title {
      font-size: 1.1rem;
      font-weight: 700;
      color: ${({ theme }) => theme.primary};
      margin-bottom: 0.5rem;
    }

    .audience-desc {
      color: ${({ theme }) => theme.textLight};
      font-size: 0.95rem;
      line-height: 1.6;
    }
  }

  @media (max-width: 768px) {
    padding: 4rem 1rem;

    .audience-grid {
      grid-template-columns: 1fr;
    }
  }
`;

function WhoIHelp() {
  const audiences = [
    {
      icon: <FaGraduationCap />,
      title: "High School Students",
      description: "AP Computer Science, advanced programming, science fair projects (ISEF), and CS competition preparation."
    },
    {
      icon: <FaBookOpen />,
      title: "College Undergraduates",
      description: "CS, data science, cybersecurity, algorithms, and math-heavy computing courses, from introductory courses to advanced upper-level coursework."
    },
    {
      icon: <FaBrain />,
      title: "Graduate Students",
      description: "Advanced coursework, coding projects, research support, and technical guidance in ML/AI and beyond."
    },
    {
      icon: <FaCode />,
      title: "Career Changers & Adult Learners",
      description: "Transitioning into software engineering, AI, data science, or cybersecurity with structured guidance."
    }
  ];

  return (
    <WhoIHelpSection>
      <div className="container">
        <motion.h2
          initial={{ y: 30 }}
          whileInView={{ y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          Who I <span>Help</span>
        </motion.h2>

        <div className="audience-grid">
          {audiences.map((audience, index) => (
            <motion.div
              key={index}
              className="audience-card"
              initial={{ y: 30 }}
              whileInView={{ y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="audience-icon" aria-hidden="true">
                {audience.icon}
              </div>
              <div>
                <div className="audience-title">{audience.title}</div>
                <div className="audience-desc">{audience.description}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </WhoIHelpSection>
  );
}


const SubjectsSection = styled.section`
  padding: 5rem 2rem;
  background: ${({ theme }) => theme.sectionBg2};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, ${({ theme }) => theme.accent}05 0%, transparent 70%);
    pointer-events: none;
  }

  .container {
    max-width: 1200px;
    margin: 0 auto;
    position: relative;
    z-index: 1;
  }

  h2 {
    font-size: clamp(2.5rem, 5vw, 3rem);
    text-align: center;
    margin-bottom: 4rem;
    font-weight: 800;
    letter-spacing: -0.03em;
    
    span {
      color: ${({ theme }) => theme.accent};
    }
  }

  .category-label {
    font-size: 1rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: ${({ theme }) => theme.accent};
    margin-bottom: 1.5rem;
    padding-left: 0.25rem;
  }

  .category-group {
    margin-bottom: 3rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .subjects-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 2rem;
  }

  .subject-card {
    background: ${({ theme }) => theme.card};
    padding: 2.5rem;
    border-radius: 24px;
    border: 1px solid ${({ theme }) => theme.borderLight};
    position: relative;
    overflow: hidden;
    transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1),
                box-shadow 0.4s cubic-bezier(0.4, 0, 0.2, 1),
                border-color 0.4s ease;
    cursor: pointer;

    &::before {
      content: '';
      position: absolute;
      inset: 0;
      background: ${({ theme }) => theme.gradient};
      opacity: 0;
      transition: opacity 0.4s ease;
      z-index: 0;
    }

    &:hover {
      transform: translateY(-6px);
      box-shadow: ${({ theme }) => theme.boxShadowHover};
      border-color: ${({ theme }) => theme.accent}66;

      &::before {
        opacity: 0.07;
      }

      h3 {
        background: ${({ theme }) => theme.gradientText};
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
        color: transparent;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      transition: border-color 0.2s ease;
      &:hover { transform: none; }
    }

    h3 {
      font-size: 1.5rem;
      margin-bottom: 1rem;
      color: ${({ theme }) => theme.primary};
      position: relative;
      z-index: 1;
      transition: color 0.3s ease;
    }

    p {
      color: ${({ theme }) => theme.textLight};
      line-height: 1.7;
      position: relative;
      z-index: 1;
      margin-bottom: 1.5rem;
    }

    .topics {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
      position: relative;
      z-index: 1;

      .topic {
        padding: 0.25rem 0.75rem;
        background: ${({ theme }) => theme.hover};
        color: ${({ theme }) => theme.text};
        border-radius: 15px;
        font-size: 0.75rem;
        font-weight: 500;
        border: 1px solid ${({ theme }) => theme.borderLight};
      }
    }

    .subject-learn-more {
      display: inline-flex;
      align-items: center;
      gap: 0.4rem;
      margin-top: 1.25rem;
      padding-top: 1rem;
      border-top: 1px solid ${({ theme }) => theme.borderLight};
      color: ${({ theme }) => theme.accent};
      font-size: 0.92rem;
      font-weight: 600;
      text-decoration: none;
      position: relative;
      z-index: 1;
      transition: gap 0.2s ease;

      svg { font-size: 0.78rem; transition: transform 0.2s ease; }
      &:hover { gap: 0.7rem; }
      &:hover svg { transform: translateX(2px); }

      &:focus-visible {
        outline: 2px solid ${({ theme }) => theme.accent};
        outline-offset: 3px;
        border-radius: 4px;
      }

      @media (prefers-reduced-motion: reduce) {
        &:hover { gap: 0.4rem; }
        &:hover svg { transform: none; }
      }
    }
  }

  @media (max-width: 768px) {
    padding: 4rem 1rem;

    .subjects-grid {
      grid-template-columns: 1fr;
      gap: 1.5rem;
    }
  }
`;

function Subjects() {
  const coreSubjects = [
    {
      slug: "tutoring-algorithms-data-structures",
      landingUrl: "/algorithms-data-structures-tutor",
      title: "Algorithms & Data Structures",
      description: "Build a solid foundation in algorithmic thinking, complexity analysis, and efficient data structure implementation.",
      topics: ["Sorting & Searching", "Graph Algorithms", "Dynamic Programming", "Tree Structures", "Complexity Analysis"]
    },
    {
      slug: "tutoring-machine-learning",
      landingUrl: "/machine-learning-ai-tutor",
      title: "Machine Learning & AI",
      description: "Master the fundamentals and advanced concepts of ML and AI, from supervised learning to neural networks and deep learning architectures.",
      topics: ["Neural Networks", "Deep Learning", "Computer Vision", "NLP", "Reinforcement Learning"]
    },
    {
      slug: "tutoring-data-science",
      landingUrl: "/data-science-tutor",
      title: "Data Science & Analytics",
      description: "Learn to extract insights from data using statistical methods, visualization techniques, and modern analytics tools.",
      topics: ["Statistical Analysis", "Data Visualization", "Python/R", "Big Data", "Predictive Modeling"]
    },
    {
      slug: "tutoring-cybersecurity",
      landingUrl: "/cybersecurity-tutor",
      title: "Cybersecurity",
      description: "Learn essential security principles, threat analysis, cryptography, and hands-on defensive strategies for real-world systems.",
      topics: ["Network Security", "Cryptography", "Threat Analysis", "Ethical Hacking", "Security Protocols"]
    },
    {
      slug: "tutoring-operating-systems-architecture",
      landingUrl: "/systems-architecture-tutor",
      title: "Operating Systems & Computer Architecture",
      description: "Master systems-level fundamentals: process and memory management, file systems, concurrency, computer organization, and pipelining. A strong foundation for undergraduate and graduate systems coursework.",
      topics: ["Process Management", "Memory Management", "File Systems", "Concurrency", "Computer Organization", "Pipelining"]
    },
    {
      slug: "tutoring-programming",
      landingUrl: "/programming-tutor",
      title: "Programming Fundamentals to Advanced Projects",
      description: "From your first lines of code to complex software systems, covering Python, Java, C/C++, and full-stack development.",
      topics: ["Python", "Java", "C/C++", "Web Development", "Software Design"]
    },
  ];

  const specializedSubjects = [
    {
      slug: "tutoring-ap-computer-science",
      landingUrl: "/ap-computer-science-tutor",
      title: "AP Computer Science",
      description: "Comprehensive preparation for AP Computer Science A and Principles exams, covering Java programming and computational thinking.",
      topics: ["Java Programming", "Object-Oriented Design", "Arrays & ArrayLists", "Algorithms", "AP Exam Prep"]
    },
    {
      slug: "tutoring-science-fair",
      landingUrl: "/science-fair-mentor",
      title: "Science Fair & Research Mentoring",
      description: "Expert guidance for ISEF, state science fairs, and research competitions, from choosing a topic to designing experiments, analyzing results, and presenting your work.",
      topics: ["ISEF Preparation", "Project Design", "Research Methodology", "Data Analysis", "Presentation & Judging"]
    },
    {
      slug: "tutoring-cs-competitions",
      landingUrl: "/cs-competitions-tutor",
      title: "CS Competitions & Hackathons",
      description: "Targeted preparation for competitive programming contests including USACO, CodeForces, and hackathons, building problem-solving speed and algorithmic thinking.",
      topics: ["USACO Prep", "Competitive Programming", "Contest Strategies", "Advanced Algorithms", "Hackathon Coaching"]
    },
  ];

  const renderSubjectCards = (subjects, delayOffset = 0) => (
    <div className="subjects-grid">
      {subjects.map((subject, index) => (
        <motion.div
          key={subject.slug}
          id={subject.slug}
          className="subject-card"
          initial={{ y: 30 }}
          whileInView={{ y: 0 }}
          transition={{ duration: 0.5, delay: (index + delayOffset) * 0.1 }}
          viewport={{ once: true }}
        >
          <h3>{subject.title}</h3>
          <p>{subject.description}</p>
          <div className="topics">
            {subject.topics.map((topic, topicIndex) => (
              <span key={topicIndex} className="topic">{topic}</span>
            ))}
          </div>
          {subject.landingUrl && (
            <a
              className="subject-learn-more"
              href={subject.landingUrl}
              aria-label={`Learn more about ${subject.title} tutoring`}
            >
              Learn more <FaArrowRight aria-hidden="true" />
            </a>
          )}
        </motion.div>
      ))}
    </div>
  );

  return (
    <SubjectsSection id="subjects">
      <div className="container">
        <motion.h2
          initial={{ y: 30 }}
          whileInView={{ y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          Core Areas I <span>Tutor</span>
        </motion.h2>

        <div className="category-group">
          <motion.div
            className="category-label"
            initial={{ y: 20 }}
            whileInView={{ y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            Core Academic Tutoring
          </motion.div>
          {renderSubjectCards(coreSubjects)}
        </div>

        <div className="category-group">
          <motion.div
            className="category-label"
            initial={{ y: 20 }}
            whileInView={{ y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            Specialized Mentoring
          </motion.div>
          {renderSubjectCards(specializedSubjects, coreSubjects.length)}
        </div>
      </div>
    </SubjectsSection>
  );
}


// Testimonial categories, hoisted to module level so subject landing pages can filter
const CAT_SYSTEMS = "Systems, Architecture & Hard Courses";
const CAT_SECURITY = "Cybersecurity & Security Labs";
const CAT_AI_ML = "AI, ML & Data Science";
const CAT_PROGRAMMING = "Programming Projects & Foundations";

const CATEGORY_ORDER = [CAT_SYSTEMS, CAT_SECURITY, CAT_AI_ML, CAT_PROGRAMMING];

const TESTIMONIALS = [
  {
    title: "Knowledgeable and Patient Tutor",
    text: "Mohamed has been incredibly helpful to me across multiple courses. He provided strong support in CMPEN 271, where we worked extensively with FPGA systems, and consistently made complex concepts much easier to understand. He has also been a great resource in other classes, such as AI, data-driven courses, and app programming, always taking the time to explain the material clearly and effectively. His patience, knowledge, and ability to break down difficult topics make him an excellent tutor.",
    student: "Satyadeep",
    lessons: 69,
    topic: "FPGA, AI, App Programming",
    category: CAT_SYSTEMS,
    rating: 5
  },
  {
    title: "Exceptional Computer Science Tutor",
    text: "I had many lessons with Mohamed, and I learned a great deal from him. Some of the topics we covered included Java polymorphism, Java GUI projects such as JavaFX, multi-threading and concurrency programming, and even building a calculator compiler in C++. What I especially appreciated about Mohamed's lessons was his dedication beyond our scheduled sessions. He often took extra time outside of class to prepare explanations and examples tailored to how I learn best.",
    student: "Spencer",
    lessons: 40,
    topic: "Java, C++, Concurrency",
    category: CAT_SYSTEMS,
    rating: 5
  },
  {
    title: "Amazing and Reliable Tutor",
    text: "Mohamed was great for meeting my needs consistently for the classes I had difficulty in! From assignments to helping me understand the cybersecurity concepts covered, he is one of the best tutors I had so far in the last few years.",
    student: "Pflugerville",
    lessons: 29,
    topic: "Cybersecurity",
    category: CAT_SECURITY,
    rating: 5
  },
  {
    title: "Knowledgeable and Patient Tutor",
    text: "I learned computer architecture and assembly language from Mohamed. He's a very patient tutor and explains concepts very well. He's also very punctual. I was very scared before my final exam and was struggling, however he helped me get my confidence back and taught me concepts very well at a good pace as per my learning style so that I could be best prepared for my final. In the end, it all worked out for me.",
    student: "Priaka",
    lessons: 23,
    topic: "Computer Architecture, Assembly",
    category: CAT_SYSTEMS,
    rating: 5
  },
  {
    title: "Extremely Knowledgeable and Patient",
    text: "I had the pleasure of taking several lessons with Mohamed, and he made learning machine learning both approachable and enjoyable. Coming in with no prior background, I was impressed by how clearly he explained complex concepts, breaking them down in a way that was easy to understand. He was also extremely patient and thorough, always ensuring I fully grasped each topic I asked about. Thanks to his guidance, I was able to make real progress.",
    student: "Ela",
    lessons: 14,
    topic: "Machine Learning",
    category: CAT_AI_ML,
    rating: 5
  },
  {
    title: "Exceptional Tutor - Highly Recommended!",
    text: "I've had the pleasure of working with Mohamed on a range of topics, including AI/Machine Learning, Data Cleaning and Validation, Langchain, Python, and Logistic Regression. His depth of knowledge, especially in mathematics, is seriously impressive. He takes the time to break things down clearly and always makes sure I truly understand the material before moving on. What really sets him apart is his patience and ability to explain complex concepts in a way that's approachable and detailed.",
    student: "Jeff",
    lessons: 8,
    topic: "AI/ML, Python, Data Science",
    category: CAT_AI_ML,
    rating: 5
  },
  {
    title: "Thank You for Your Support",
    text: "I learned more than I expected with Mohamed. He always gave me his full attention and made sure I truly understood the material. He genuinely cares about his students' success and encouraged me to keep working hard and not give up. He was always on time, polite, and respectful. I'm very grateful for everything I learned from him, and he inspired me to keep learning more.",
    student: "Sahara",
    lessons: 12,
    topic: "Dedicated Support",
    category: CAT_PROGRAMMING,
    rating: 5
  },
  {
    title: "Knowledgeable and Attentive",
    text: "Mohammed is an exceptional tutor in Business Applied Decisions. His deep subject matter expertise and dedication to student success are evident in every session. I have engaged him for multiple lessons and am consistently impressed by his professionalism and the quality of his work. I highly recommend him.",
    student: "Ayisha",
    lessons: 11,
    topic: "Business Applied Decisions",
    category: CAT_PROGRAMMING,
    rating: 5
  },
  {
    title: "Knowledgeable and Awesome",
    text: "It was amazing!! He knew absolutely everything I needed for my assignment. I am a novice taking programming so now I feel like I can do it. It was such nice session. Mohamed is patient and kind.",
    student: "Agita",
    lessons: 11,
    topic: "Programming Basics",
    category: CAT_PROGRAMMING,
    rating: 5
  },
  {
    title: "Never Gave Up",
    text: "A lot of assignments I had required me to install a lot of new software and IDE's which he was great at helping me set up and find why things weren't working. Even if it took the entire lesson, there was always some progress made. My professor really just throws us to the wolves with a video someone else made or a document that explains one aspect of assignment and the rest you got to figure out yourself. I would not have gotten as far as I did in that course without Mohammed setting me up for success.",
    student: "Juedeja",
    lessons: 7,
    topic: "Setup & Configuration",
    category: CAT_PROGRAMMING,
    rating: 5
  },
  {
    title: "Excellent Cybersecurity Tutor",
    text: "We worked extensively through several SEED cyber security labs. He has a gentle and patient style. Sessions focused on both learning and achieving success on each assignment. I would recommend tutoring with Mohamed.",
    student: "Adam",
    lessons: 6,
    topic: "Cybersecurity Labs",
    category: CAT_SECURITY,
    rating: 5
  },
  {
    title: "Knowledgeable in Cursor 2.0",
    text: "Helped me learn how to use AI agents and understand the new features of Cursor 2. One of the very few people up to date on this technology. Thank you for the help!",
    student: "Sasha",
    lessons: 2,
    topic: "AI Tools",
    category: CAT_AI_ML,
    rating: 5
  },
  {
    title: "Excellence - HIGHLY Recommend",
    text: "Outstanding, Mohamed is helping me with a computer science and Wireshark college course. He is extremely knowledgeable and patient, and has provided me with additional resources to assist with assignments and projects. I'm grateful I found him!",
    student: "Vivian",
    lessons: 2,
    topic: "Wireshark, Networking",
    category: CAT_SECURITY,
    rating: 5
  },
  {
    title: "Committed Tutor for Success",
    text: "I had the privilege of working with Mohamed, and I can confidently say that he went above and beyond to help me successfully complete multiple complex projects. Not only did he provide detailed guidance at every step, but he also ensured that I understood the finer details and concepts, which made all the difference in my learning experience. His patience, expertise, and personalized approach made even the most challenging topics feel manageable.",
    student: "Jalen",
    lessons: 2,
    topic: "Complex Projects",
    category: CAT_PROGRAMMING,
    rating: 5
  }
];


const TestimonialsSection = styled.section`
  padding: 5rem 2rem;
  background: ${({ theme }) => theme.sectionBg1};
  overflow: hidden;

  .container {
    max-width: 1200px;
    margin: 0 auto;
  }

  h2 {
    font-size: clamp(2.5rem, 5vw, 3rem);
    text-align: center;
    margin-bottom: 1rem;
    font-weight: 800;
    letter-spacing: -0.03em;
    
    span {
      color: ${({ theme }) => theme.accent};
    }
  }

  .section-subtitle {
    text-align: center;
    color: ${({ theme }) => theme.textLight};
    font-size: 1.1rem;
    margin-bottom: 3rem;
  }

  .carousel-wrapper {
    position: relative;
    padding: 0 50px;

    @media (max-width: 768px) {
      padding: 0 10px;
    }
  }

  .carousel-track {
    display: flex;
    gap: 1.5rem;
    transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .testimonial-slide {
    flex: 0 0 calc(33.333% - 1rem);
    min-width: 0;

    @media (max-width: 1024px) {
      flex: 0 0 calc(50% - 0.75rem);
    }

    @media (max-width: 768px) {
      flex: 0 0 100%;
    }
  }

  .testimonial-card {
    background: ${({ theme }) => theme.card};
    padding: 2rem;
    border-radius: 20px;
    border: 1px solid ${({ theme }) => theme.borderLight};
    height: 100%;
    display: flex;
    flex-direction: column;
    position: relative;
    transition: all 0.3s ease;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 4px;
      background: ${({ theme }) => theme.gradient};
      border-radius: 4px 0 0 4px;
      transform: scaleY(0);
      transition: transform 0.3s ease;
    }

    &:hover {
      transform: translateY(-5px);
      box-shadow: ${({ theme }) => theme.boxShadowHover};
      
      &::before {
        transform: scaleY(1);
      }
    }

    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 1rem;
      gap: 1rem;
    }

    .stars {
      display: flex;
      gap: 0.2rem;
      flex-shrink: 0;

      svg {
        color: #ffa726;
        font-size: 1rem;
      }
    }

    .lesson-badge {
      background: ${({ theme }) => theme.hover};
      color: ${({ theme }) => theme.accent};
      padding: 0.25rem 0.75rem;
      border-radius: 20px;
      font-size: 0.75rem;
      font-weight: 600;
      white-space: nowrap;
      border: 1px solid ${({ theme }) => theme.borderLight};
    }

    .testimonial-title {
      font-size: 1.1rem;
      font-weight: 700;
      color: ${({ theme }) => theme.primary};
      margin-bottom: 0.75rem;
      line-height: 1.4;
    }

    .testimonial-text {
      color: ${({ theme }) => theme.textLight};
      line-height: 1.7;
      font-size: 0.95rem;
      flex-grow: 1;
      margin-bottom: 1.25rem;
      display: -webkit-box;
      -webkit-line-clamp: 6;
      -webkit-box-orient: vertical;
      overflow: hidden;

      &.expanded {
        -webkit-line-clamp: unset;
        overflow: visible;
      }
    }

    .read-more-btn {
      background: none;
      border: none;
      color: ${({ theme }) => theme.accent};
      font-size: 0.85rem;
      font-weight: 600;
      cursor: pointer;
      padding: 0;
      margin-bottom: 1rem;
      transition: opacity 0.2s;

      &:hover {
        opacity: 0.8;
      }
    }

    .student-info {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      padding-top: 1rem;
      border-top: 1px solid ${({ theme }) => theme.borderLight};
      margin-top: auto;

      .student-avatar {
        width: 42px;
        height: 42px;
        background: ${({ theme }) => theme.gradient};
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: 700;
        font-size: 1.1rem;
        flex-shrink: 0;
      }

      .student-details {
        .student-name {
          font-weight: 600;
          color: ${({ theme }) => theme.primary};
          font-size: 0.95rem;
        }

        .topic-tag {
          font-size: 0.8rem;
          color: ${({ theme }) => theme.textLight};
        }
      }
    }
  }

  .carousel-nav {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: ${({ theme }) => theme.card};
    border: 1px solid ${({ theme }) => theme.border};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.3s ease;
    color: ${({ theme }) => theme.text};
    z-index: 10;
    box-shadow: ${({ theme }) => theme.boxShadow};

    &:hover:not(:disabled) {
      background: ${({ theme }) => theme.accent};
      color: white;
      border-color: ${({ theme }) => theme.accent};
      transform: translateY(-50%) scale(1.05);
    }

    &:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }

    &.prev {
      left: 0;
    }

    &.next {
      right: 0;
    }

    @media (max-width: 768px) {
      width: 36px;
      height: 36px;
      
      &.prev {
        left: -5px;
      }
      
      &.next {
        right: -5px;
      }
    }
  }

  .carousel-dots {
    display: flex;
    justify-content: center;
    gap: 0.5rem;
    margin-top: 2rem;

    .dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: ${({ theme }) => theme.border};
      border: none;
      cursor: pointer;
      transition: all 0.3s ease;
      padding: 0;

      &.active {
        background: ${({ theme }) => theme.accent};
        transform: scale(1.2);
      }

      &:hover:not(.active) {
        background: ${({ theme }) => theme.textLight};
      }
    }
  }

  .view-all-container {
    text-align: center;
    margin-top: 2.75rem;

    .view-all-btn {
      display: inline-flex;
      align-items: center;
      gap: 0.6rem;
      padding: 1rem 2rem;
      background: ${({ theme }) => theme.gradient};
      color: white;
      border: none;
      border-radius: 12px;
      font-weight: 700;
      font-size: 1.05rem;
      letter-spacing: -0.01em;
      cursor: pointer;
      transition: transform 0.3s ease, box-shadow 0.3s ease;
      box-shadow: 0 6px 22px -4px ${({ theme }) => theme.accent}66;

      &:hover {
        transform: translateY(-3px);
        box-shadow: 0 14px 38px -6px ${({ theme }) => theme.accent}99;
      }

      &:focus-visible {
        outline: 2px solid ${({ theme }) => theme.accent};
        outline-offset: 4px;
      }

      @media (prefers-reduced-motion: reduce) {
        &:hover { transform: none; }
      }

      svg {
        transition: transform 0.3s ease;
      }

      &:hover svg {
        transform: translateX(4px);
      }
    }

    .view-all-secondary {
      margin-top: 1rem;
      font-size: 0.92rem;
      color: ${({ theme }) => theme.textLight};

      a {
        color: ${({ theme }) => theme.accent};
        font-weight: 600;
        text-decoration: none;
        border-bottom: 1px solid ${({ theme }) => theme.accent}66;
        transition: border-color 0.2s ease;

        &:hover {
          border-color: ${({ theme }) => theme.accent};
        }

        &:focus-visible {
          outline: 2px solid ${({ theme }) => theme.accent};
          outline-offset: 3px;
        }
      }
    }
  }

  @media (max-width: 768px) {
    padding: 4rem 1rem;
  }
`;

const TestimonialsModal = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(5px);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;

  .modal-content {
    background: ${({ theme }) => theme.card};
    border-radius: 24px;
    max-width: 900px;
    width: 100%;
    max-height: 85vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    border: 1px solid ${({ theme }) => theme.border};
  }

  .modal-header {
    padding: 1.5rem 2rem;
    border-bottom: 1px solid ${({ theme }) => theme.borderLight};
    display: flex;
    justify-content: space-between;
    align-items: center;

    h3 {
      font-size: 1.5rem;
      font-weight: 700;
      color: ${({ theme }) => theme.primary};
      margin: 0;
    }

    .close-btn {
      width: 40px;
      height: 40px;
      border-radius: 10px;
      background: ${({ theme }) => theme.hover};
      border: 1px solid ${({ theme }) => theme.border};
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      color: ${({ theme }) => theme.text};
      transition: all 0.2s ease;

      &:hover {
        background: ${({ theme }) => theme.accent};
        color: white;
        border-color: ${({ theme }) => theme.accent};
      }
    }
  }

  .modal-body {
    padding: 2rem;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 2.5rem;

    @media (max-width: 768px) {
      padding: 1rem;
      gap: 2rem;
    }
  }

  .category-group {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
    gap: 1.25rem;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  .category-heading {
    grid-column: 1 / -1;
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 1rem;
    font-size: 1.05rem;
    font-weight: 700;
    color: ${({ theme }) => theme.accent};
    letter-spacing: 0.04em;
    text-transform: uppercase;
    padding-bottom: 0.6rem;
    border-bottom: 1px solid ${({ theme }) => theme.borderLight};
    margin: 0;
  }

  .category-count {
    font-size: 0.78rem;
    font-weight: 600;
    text-transform: none;
    letter-spacing: 0;
    color: ${({ theme }) => theme.textLight};
    padding: 0.2rem 0.65rem;
    background: ${({ theme }) => theme.accent}1A;
    border-radius: 50px;
    flex-shrink: 0;
  }

  .modal-card {
    background: ${({ theme }) => theme.body};
    padding: 1.5rem;
    border-radius: 16px;
    border: 1px solid ${({ theme }) => theme.borderLight};

    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      margin-bottom: 0.75rem;
      gap: 1rem;
    }

    .stars {
      display: flex;
      gap: 0.2rem;

      svg {
        color: #ffa726;
        font-size: 0.9rem;
      }
    }

    .lesson-badge {
      background: ${({ theme }) => theme.hover};
      color: ${({ theme }) => theme.accent};
      padding: 0.2rem 0.6rem;
      border-radius: 15px;
      font-size: 0.7rem;
      font-weight: 600;
    }

    .testimonial-title {
      font-size: 1rem;
      font-weight: 700;
      color: ${({ theme }) => theme.primary};
      margin-bottom: 0.5rem;
    }

    .testimonial-text {
      color: ${({ theme }) => theme.textLight};
      line-height: 1.6;
      font-size: 0.9rem;
      margin-bottom: 1rem;
    }

    .student-info {
      display: flex;
      align-items: center;
      gap: 0.6rem;
      padding-top: 0.75rem;
      border-top: 1px solid ${({ theme }) => theme.borderLight};

      .student-avatar {
        width: 36px;
        height: 36px;
        background: ${({ theme }) => theme.gradient};
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: 700;
        font-size: 1rem;
      }

      .student-name {
        font-weight: 600;
        color: ${({ theme }) => theme.primary};
        font-size: 0.9rem;
      }

      .topic-tag {
        font-size: 0.75rem;
        color: ${({ theme }) => theme.textLight};
      }
    }
  }
`;

function Testimonials() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [expandedCards, setExpandedCards] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [slidesPerView, setSlidesPerView] = useState(3);
  const [slideWidth, setSlideWidth] = useState(0);
  const carouselViewportRef = useRef(null);

  const testimonials = TESTIMONIALS;

  const categoryOrder = [CAT_SYSTEMS, CAT_SECURITY, CAT_AI_ML, CAT_PROGRAMMING];
  const testimonialsByCategory = categoryOrder.map(cat => ({
    name: cat,
    items: testimonials.filter(t => t.category === cat)
  }));

  useEffect(() => {
    const updateSlideWidth = () => {
      requestAnimationFrame(() => {
        const firstSlide = carouselViewportRef.current?.querySelector('.testimonial-slide');
        if (firstSlide) {
          const styles = window.getComputedStyle(firstSlide);
          const marginRight = parseFloat(styles.marginRight) || 0;
          setSlideWidth(firstSlide.getBoundingClientRect().width + marginRight + 24);
        }
      });
    };

    const handleResize = () => {
      if (window.innerWidth <= 768) {
        setSlidesPerView(1);
      } else if (window.innerWidth <= 1024) {
        setSlidesPerView(2);
      } else {
        setSlidesPerView(3);
      }
      updateSlideWidth();
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const firstSlide = carouselViewportRef.current?.querySelector('.testimonial-slide');
    if (firstSlide) {
      setSlideWidth(firstSlide.getBoundingClientRect().width + 24);
    }
  }, [slidesPerView, testimonials.length]);

  useEffect(() => {
    if (isPaused || showModal) return;

    const interval = setInterval(() => {
      setCurrentIndex(prev => {
        const maxIndex = testimonials.length - slidesPerView;
        return prev >= maxIndex ? 0 : prev + 1;
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [isPaused, showModal, slidesPerView, testimonials.length]);

  const maxIndex = testimonials.length - slidesPerView;

  const goToPrev = () => {
    setCurrentIndex(prev => (prev <= 0 ? maxIndex : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex(prev => (prev >= maxIndex ? 0 : prev + 1));
  };

  const goToSlide = (index) => {
    setCurrentIndex(Math.min(index, maxIndex));
  };

  const toggleExpand = (index) => {
    setExpandedCards(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const totalDots = Math.ceil(testimonials.length / slidesPerView);

  return (
    <TestimonialsSection id="testimonials">
      <div className="container">
        <motion.h2
          initial={{ y: 30 }}
          whileInView={{ y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          Student <span>Success Stories</span>
        </motion.h2>

        <motion.p
          className="section-subtitle"
          initial={{ y: 20 }}
          whileInView={{ y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          viewport={{ once: true }}
        >
          Real results from students in difficult programming, cybersecurity, AI/ML, architecture, and systems courses
        </motion.p>

        <motion.div
          className="carousel-wrapper"
          initial={{ y: 30 }}
          whileInView={{ y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <button
            className="carousel-nav prev"
            onClick={goToPrev}
            aria-label="Previous testimonials"
          >
            <FaArrowRight style={{ transform: 'rotate(180deg)' }} />
          </button>

          <div style={{ overflow: 'hidden' }} ref={carouselViewportRef}>
            <div
              className="carousel-track"
              style={{
                transform: `translateX(-${currentIndex * slideWidth}px)`
              }}
            >
              {testimonials.map((testimonial, index) => (
                <div key={index} className="testimonial-slide">
                  <div className="testimonial-card">
                    <div className="card-header">
                      <div className="stars">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <FaStar key={i} />
                        ))}
                      </div>
                      <span className="lesson-badge">
                        {testimonial.lessons} lesson{testimonial.lessons !== 1 ? 's' : ''}
                      </span>
                    </div>
                    
                    <h3 className="testimonial-title">{testimonial.title}</h3>
                    
                    <p className={`testimonial-text ${expandedCards[index] ? 'expanded' : ''}`}>
                      "{testimonial.text}"
                    </p>

                    {testimonial.text.length > 200 && (
                      <button
                        className="read-more-btn"
                        onClick={() => toggleExpand(index)}
                        aria-expanded={!!expandedCards[index]}
                      >
                        {expandedCards[index] ? 'Show less' : 'Read more'}
                      </button>
                    )}

                    <div className="student-info">
                      <div className="student-avatar">
                        {testimonial.student.charAt(0)}
                      </div>
                      <div className="student-details">
                        <div className="student-name">{testimonial.student}</div>
                        <div className="topic-tag">{testimonial.topic}</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <button
            className="carousel-nav next"
            onClick={goToNext}
            aria-label="Next testimonials"
          >
            <FaArrowRight />
          </button>
        </motion.div>

        <div className="carousel-dots">
          {[...Array(totalDots)].map((_, index) => (
            <button
              key={index}
              className={`dot ${Math.floor(currentIndex / slidesPerView) === index ? 'active' : ''}`}
              onClick={() => goToSlide(index * slidesPerView)}
              aria-label={`Go to slide group ${index + 1}`}
            />
          ))}
        </div>

        <div className="view-all-container">
          <a
            className="view-all-btn"
            href="https://www.wyzant.com/Tutors/MoTutoring"
            target="_blank"
            rel="noopener noreferrer"
          >
            View Verified Wyzant Reviews <FaArrowRight aria-hidden="true" />
          </a>
          <div className="view-all-secondary">
            Rated 5.0 across 250+ ratings from verified tutoring sessions on Wyzant.
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showModal && (
          <TestimonialsModal
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={(e) => {
              if (e.target === e.currentTarget) setShowModal(false);
            }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
            onKeyDown={(e) => {
              if (e.key === 'Escape') setShowModal(false);
            }}
          >
            <motion.div
              className="modal-content"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            >
              <div className="modal-header">
                <h3 id="modal-title">All Student Reviews</h3>
                <button
                  className="close-btn"
                  onClick={() => setShowModal(false)}
                  aria-label="Close reviews modal"
                >
                  <FiX aria-hidden="true" />
                </button>
              </div>

              <div className="modal-body">
                {testimonialsByCategory.map((group) => (
                  group.items.length > 0 && (
                    <section key={group.name} className="category-group" aria-label={group.name}>
                      <h4 className="category-heading">
                        {group.name}
                        <span className="category-count">{group.items.length} review{group.items.length !== 1 ? 's' : ''}</span>
                      </h4>
                      {group.items.map((testimonial, index) => (
                        <div key={index} className="modal-card">
                          <div className="card-header">
                            <div className="stars">
                              {[...Array(testimonial.rating)].map((_, i) => (
                                <FaStar key={i} />
                              ))}
                            </div>
                            <span className="lesson-badge">
                              {testimonial.lessons} lesson{testimonial.lessons !== 1 ? 's' : ''}
                            </span>
                          </div>

                          <h5 className="testimonial-title">{testimonial.title}</h5>
                          <p className="testimonial-text">"{testimonial.text}"</p>

                          <div className="student-info">
                            <div className="student-avatar">
                              {testimonial.student.charAt(0)}
                            </div>
                            <div>
                              <div className="student-name">{testimonial.student}</div>
                              <div className="topic-tag">{testimonial.topic}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </section>
                  )
                ))}
              </div>
            </motion.div>
          </TestimonialsModal>
        )}
      </AnimatePresence>
    </TestimonialsSection>
  );
}


const ContactSection = styled.section`
  padding: 5rem 2rem;
  background: ${({ theme }) => theme.sectionBg2};

  .container {
    max-width: 800px;
    margin: 0 auto;
  }

  h2 {
    font-size: clamp(2.5rem, 5vw, 3rem);
    text-align: center;
    margin-bottom: 1rem;
    font-weight: 800;
    letter-spacing: -0.03em;
  }

  .subtitle {
    text-align: center;
    color: ${({ theme }) => theme.textLight};
    font-size: 1.125rem;
    margin-bottom: 2rem;
    line-height: 1.6;
  }

  .contact-tabs {
    display: flex;
    max-width: 480px;
    margin: 0 auto 2rem;
    background: ${({ theme }) => theme.card};
    padding: 0.375rem;
    border-radius: 16px;
    border: 1px solid ${({ theme }) => theme.borderLight};
  }

  .contact-tab {
    flex: 1;
    padding: 0.75rem 1.25rem;
    border: none;
    background: transparent;
    color: ${({ theme }) => theme.textLight};
    font-size: 0.95rem;
    font-weight: 600;
    cursor: pointer;
    border-radius: 12px;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    font-family: inherit;

    &.active {
      background: ${({ theme }) => theme.gradient};
      color: white;
      box-shadow: 0 2px 10px -1px ${({ theme }) => theme.accent}33;
    }

    &:not(.active):hover {
      color: ${({ theme }) => theme.primary};
      background: ${({ theme }) => theme.hover};
    }

    svg {
      font-size: 0.95rem;
    }
  }

  .calendly-container {
    background: ${({ theme }) => theme.card};
    border-radius: 24px;
    border: 1px solid ${({ theme }) => theme.borderLight};
    box-shadow: ${({ theme }) => theme.boxShadow};
    overflow: hidden;
    min-height: 700px;
  }

  .form-container {
    background: ${({ theme }) => theme.card};
    padding: 3rem;
    border-radius: 24px;
    border: 1px solid ${({ theme }) => theme.borderLight};
    box-shadow: ${({ theme }) => theme.boxShadow};
  }

  .form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;
    margin-bottom: 1.5rem;

    .form-group {
      &.full-width {
        grid-column: 1 / -1;
      }
    }
  }

  .form-group {
    label {
      display: block;
      font-weight: 600;
      color: ${({ theme }) => theme.primary};
      margin-bottom: 0.5rem;
      font-size: 0.95rem;
    }

    input, select, textarea {
      width: 100%;
      padding: 0.875rem 1rem;
      border: 2px solid ${({ theme }) => theme.border};
      border-radius: 12px;
      background: ${({ theme }) => theme.body};
      color: ${({ theme }) => theme.text};
      font-size: 1rem;
      transition: all 0.3s ease;
      font-family: inherit;

      &:focus {
        outline: none;
        border-color: ${({ theme }) => theme.accent};
        box-shadow: 0 0 0 3px ${({ theme }) => theme.accent}20;
      }

      &::placeholder {
        color: ${({ theme }) => theme.textLight};
      }

      &[aria-invalid="true"] {
        border-color: #ff4444;
      }

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        background: ${({ theme }) => theme.borderLight};
      }
    }

    textarea {
      resize: vertical;
      min-height: 120px;
      line-height: 1.5;
    }

    .field-error {
      color: #ff4444;
      font-size: 0.875rem;
      margin-top: 0.25rem;
      display: block;
    }
  }

  .submit-btn {
    width: 100%;
    padding: 1rem;
    background: ${({ theme }) => theme.gradient};
    color: white;
    border: none;
    border-radius: 12px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    min-height: 56px;

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 10px 30px -4px ${({ theme }) => theme.accent}4D;
    }

    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
      transform: none;
    }

    &:focus {
      outline: 2px solid ${({ theme }) => theme.accent};
      outline-offset: 2px;
    }
  }

  .success-message, .error-message, .rate-limit-notice {
    padding: 1rem;
    border-radius: 12px;
    text-align: center;
    margin-top: 1rem;
    border: 1px solid;
  }

  .success-message {
    background: #00c85315;
    color: #00c853;
    border-color: #00c85330;
  }

  .error-message {
    background: #ff444415;
    color: #ff4444;
    border-color: #ff444430;
  }

  .rate-limit-notice {
    background: #ff944415;
    color: #ff9444;
    border-color: #ff944430;
    margin-bottom: 1rem;
    margin-top: 0;
    font-size: 0.9rem;
  }

  .loading-spinner {
    width: 20px;
    height: 20px;
    border: 2px solid white;
    border-top: 2px solid transparent;
    border-radius: 50%;
  }

  @media (max-width: 768px) {
    padding: 4rem 1rem;

    .contact-tab {
      padding: 0.625rem 0.75rem;
      font-size: 0.85rem;
    }

    .calendly-container {
      min-height: 650px;
      border-radius: 16px;
    }

    .form-container {
      padding: 2rem;
    }

    .form-grid {
      grid-template-columns: 1fr;
      gap: 1rem;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .submit-btn:hover {
      transform: none;
    }
    
    .loading-spinner {
      animation: none;
      border: 2px solid white;
    }
  }
`;


const EMAILJS_CONFIG = {
  PUBLIC_KEY: process.env.REACT_APP_EMAILJS_PUBLIC_KEY,
  SERVICE_ID: process.env.REACT_APP_EMAILJS_SERVICE_ID,
  TEMPLATE_ID: process.env.REACT_APP_EMAILJS_TEMPLATE_ID
};

// Validate environment variables
const validateEnvVars = () => {
  const requiredEnvVars = [
    'REACT_APP_EMAILJS_PUBLIC_KEY',
    'REACT_APP_EMAILJS_SERVICE_ID', 
    'REACT_APP_EMAILJS_TEMPLATE_ID',
    'REACT_APP_FORMSPREE_FORM_ID'
  ];
  
  const missingVars = requiredEnvVars.filter(varName => !process.env[varName]);
  
  if (missingVars.length > 0) {
    console.error('Missing required environment variables:', missingVars);
    return false;
  }
  
  return true;
};


if (process.env.NODE_ENV === 'development') {
  if (!validateEnvVars()) {
    console.warn('Some environment variables are missing. Please check your .env file.');
  }
}


const CONFIG = {
  RATE_LIMIT: {
    MAX_SUBMISSIONS: 3,
    TIME_WINDOW: 60 * 60 * 1000, 
    STORAGE_KEY: 'form_submissions_timestamp'
  },
  FORM: {
    TIMEOUT: 30000,
    RETRY_ATTEMPTS: 2
  },
  VALIDATION: {
    EMAIL_REGEX: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
    PHONE_REGEX: /^[\+]?[1-9][\d]{0,15}$/,
    MIN_TEXT_LENGTH: 10,
    MAX_TEXT_LENGTH: 2000
  }
};


const EmailService = {
  isInitialized: false,

  async initialize() {
    if (this.isInitialized) return;
    
    try {
  
      if (typeof window !== 'undefined' && !window.emailjs) {
        await this.loadEmailJS();
      }
      
      if (window.emailjs) {
        window.emailjs.init(EMAILJS_CONFIG.PUBLIC_KEY);
        this.isInitialized = true;
        console.log('EmailJS initialized successfully');
      }
    } catch (error) {
      console.error('Failed to initialize EmailJS:', error);
      throw new Error('Email service initialization failed');
    }
  },

  async loadEmailJS() {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/@emailjs/browser@3/dist/email.min.js';
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  },

  async sendConfirmationEmail(formData) {
    await this.initialize();

    if (!window.emailjs) {
      throw new Error('EmailJS not available');
    }


    if (!validateEnvVars()) {
      throw new Error('EmailJS configuration incomplete. Please check environment variables.');
    }

    const templateParams = {
      to_email: formData.email,
      to_name: `${formData.firstName} ${formData.lastName}`,
      first_name: formData.firstName,
      last_name: formData.lastName,
      email: formData.email,
      subject_interest: formData.subject,
      current_level: formData.currentLevel,
      learning_goals: formData.goals,
      phone_number: formData.phone || 'Not provided',
      availability: formData.availability || 'Not specified',
      additional_info: formData.additionalInfo || 'None provided',
      urgent_request: formData.urgentRequest || 'None',
      reply_to: formData.email
    };

    try {
      console.log('Sending email with params:', templateParams);
      
      const response = await window.emailjs.send(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.TEMPLATE_ID,
        templateParams
      );
      
      console.log('Confirmation email sent successfully:', response);
      return response;
    } catch (error) {
      console.error('EmailJS send error details:', error);
      
      if (error.status === 422) {
        throw new Error('Invalid template or missing required fields. Please check your EmailJS template configuration.');
      } else if (error.status === 400) {
        throw new Error('Bad request. Please check your EmailJS service configuration.');
      } else if (error.status === 401) {
        throw new Error('Unauthorized. Please check your EmailJS public key.');
      } else {
        throw new Error(`Email service error (${error.status || 'unknown'}): ${error.message || 'Unknown error'}`);
      }
    }
  }
};


const sanitizeInput = (input, options = {}) => {
  if (typeof input !== 'string') return input;
  return input.replace(/[<>]/g, '');
};

const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const validatePhone = (phone) => {
  const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
  return phoneRegex.test(phone.replace(/[\s\-\(\)]/g, ''));
};


const RATE_LIMIT = { MAX_SUBMISSIONS: 3, WINDOW_MS: 60 * 60 * 1000 };
const RATE_LIMIT_KEY = 'tutoring_form_submissions';

const useRateLimit = () => {
  const getSubmissions = useCallback(() => {
    try {
      const raw = localStorage.getItem(RATE_LIMIT_KEY);
      if (!raw) return [];
      const entries = JSON.parse(raw);
      const cutoff = Date.now() - RATE_LIMIT.WINDOW_MS;
      return entries.filter(ts => ts > cutoff);
    } catch {
      return [];
    }
  }, []);

  const checkRateLimit = useCallback(() => {
    const recent = getSubmissions();
    return recent.length < RATE_LIMIT.MAX_SUBMISSIONS;
  }, [getSubmissions]);

  const recordSubmission = useCallback(() => {
    const recent = getSubmissions();
    recent.push(Date.now());
    localStorage.setItem(RATE_LIMIT_KEY, JSON.stringify(recent));
  }, [getSubmissions]);

  const getRemainingSubmissions = useCallback(() => {
    const recent = getSubmissions();
    return Math.max(0, RATE_LIMIT.MAX_SUBMISSIONS - recent.length);
  }, [getSubmissions]);

  return { checkRateLimit, recordSubmission, getRemainingSubmissions };
};


const useFormValidation = (formData) => {
  return useMemo(() => {
    const errors = {};

    const requiredFields = {
      firstName: 'Name',
      email: 'Email address',
      goals: 'What you are stuck on'
    };

    Object.entries(requiredFields).forEach(([key, label]) => {
      const value = sanitizeInput(formData[key]);
      if (!value) {
        errors[key] = `${label} is required`;
      }
    });

    if (formData.email && !errors.email && !validateEmail(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (formData.phone && !validatePhone(formData.phone)) {
      errors.phone = 'Please enter a valid phone number';
    }

    return {
      errors,
      isValid: Object.keys(errors).length === 0
    };
  }, [formData]);
};


const submitFormData = async (formData, retryCount = 0) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), CONFIG.FORM.TIMEOUT);

  try {

    const csrfToken = 'temp-token';
    const sanitizedData = formData;


    const formspreeFormId = process.env.REACT_APP_FORMSPREE_FORM_ID;
    if (!formspreeFormId) {
      throw new Error('Formspree form ID not configured. Please check environment variables.');
    }


    const response = await fetch(`https://formspree.io/f/${formspreeFormId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(sanitizedData),
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      if (response.status === 429) {
        throw new Error('Too many requests. Please try again later.');
      }
      if (response.status === 422) {
        throw new Error('Please check your entries. Make sure your email address is valid and all required fields are filled in correctly.');
      }
      
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Something went wrong. Please try again or contact me directly.');
    }

    return await response.json();

  } catch (error) {
    clearTimeout(timeoutId);
    
    if (error.name === 'AbortError') {
      throw new Error('Request timed out. Please check your connection and try again.');
    }
    
    if (retryCount < CONFIG.FORM.RETRY_ATTEMPTS && !error.message.includes('Too many requests')) {
      await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
      return submitFormData(formData, retryCount + 1);
    }
    
    throw error;
  }
};

function Contact({ theme }) {
  const [contactMode, setContactMode] = useState('booking');

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    subject: '',
    currentLevel: '',
    goals: '',
    availability: '',
    additionalInfo: '',
    urgentRequest: ''
  });

  const [submissionState, setSubmissionState] = useState({
    isSubmitting: false,
    isSubmitted: false,
    error: null,
    isRateLimited: false,
    emailSent: false,
    emailError: null
  });

  const { checkRateLimit, recordSubmission, getRemainingSubmissions } = useRateLimit();
  const { errors, isValid } = useFormValidation(formData);
  const [submitted, setSubmitted] = useState(false);
  const formRef = useRef();

  const calendlyUrl = theme === 'dark'
    ? 'https://calendly.com/mohamedabdelnaby/free-consultation?hide_gdpr_banner=1&background_color=1a1a1a&text_color=ffffff&primary_color=6b9fff'
    : 'https://calendly.com/mohamedabdelnaby/free-consultation?hide_gdpr_banner=1&background_color=ffffff&text_color=1a1a1a&primary_color=0055d4';

  useEffect(() => {
    if (contactMode !== 'booking') return;

    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);

    const handleCalendlyEvent = (e) => {
      if (e.data && e.data.event === 'calendly.event_scheduled') {
        if (typeof window !== 'undefined' && window.fbq) {
          window.fbq('track', 'Lead');
        }
        if (typeof window !== 'undefined' && window.gtag) {
          window.gtag('event', 'form_submit', {
            event_category: 'engagement',
            event_label: 'calendly_booking'
          });
        }
      }
    };

    window.addEventListener('message', handleCalendlyEvent);

    return () => {
      script.remove();
      window.removeEventListener('message', handleCalendlyEvent);
    };
  }, [contactMode, theme]);

  useEffect(() => {
    EmailService.initialize().catch(error => {
      console.warn('EmailJS initialization failed:', error);
    });
  }, []);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    

    if (submissionState.error || submissionState.isRateLimited) {
      setSubmissionState(prev => ({
        ...prev,
        error: null,
        isRateLimited: false,
        emailError: null
      }));
    }
  }, [submissionState.error, submissionState.isRateLimited]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setSubmitted(true);

    setSubmissionState(prev => ({ 
      ...prev, 
      error: null, 
      isRateLimited: false,
      emailError: null,
      emailSent: false
    }));


    if (!checkRateLimit()) {
      setSubmissionState(prev => ({
        ...prev,
        isRateLimited: true,
        error: 'Submission limit reached. Please try again in an hour.'
      }));
      return;
    }


    if (!isValid) {
      const firstErrorField = Object.keys(errors)[0];
      const errorElement = document.getElementById(firstErrorField);
      errorElement?.focus();
      return;
    }

    setSubmissionState(prev => ({ ...prev, isSubmitting: true }));

    try {

      await submitFormData(formData);
      
      recordSubmission();


      try {
        await EmailService.sendConfirmationEmail(formData);
        setSubmissionState(prev => ({
          ...prev,
          isSubmitting: false,
          isSubmitted: true,
          emailSent: true
        }));
      } catch (emailError) {
        console.error('Email sending failed:', emailError);
        setSubmissionState(prev => ({
          ...prev,
          isSubmitting: false,
          isSubmitted: true,
          emailSent: false,
          emailError: 'Form submitted successfully, but confirmation email failed to send.'
        }));
      }


      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'form_submit', {
          event_category: 'engagement',
          event_label: 'contact_form'
        });
      }

      if (typeof window !== 'undefined' && window.fbq) {
        window.fbq('track', 'Lead');
      }

    } catch (error) {
      console.error('Form submission failed:', error);
      
      setSubmissionState(prev => ({
        ...prev,
        isSubmitting: false,
        error: error.message || 'Failed to submit form. Please try again or contact me directly.'
      }));


      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'form_error', {
          event_category: 'error',
          event_label: error.message
        });
      }
    }
  }, [formData, isValid, errors, checkRateLimit, recordSubmission]);

  const remainingSubmissions = getRemainingSubmissions();

  if (submissionState.isSubmitted) {
    return (
      <ContactSection id="contact">
        <div className="container">
          <motion.div
            initial={{ y: 30 }}
            animate={{ y: 0 }}
            className="form-container"
            style={{ textAlign: 'center' }}
          >
            <div style={{ fontSize: '3rem', color: '#00c853', marginBottom: '1rem' }} aria-hidden="true">✓</div>
            <h2>Thank You!</h2>
            <p>Your request has been submitted successfully.</p>
            
            {submissionState.emailSent ? (
              <p style={{ marginTop: '1rem', color: '#00c853' }}>
                A confirmation email has been sent to {formData.email}.
              </p>
            ) : submissionState.emailError ? (
              <p style={{ marginTop: '1rem', color: '#ff9444' }}>
                {submissionState.emailError}
              </p>
            ) : null}
            
            <p style={{ marginTop: '1rem', color: '#666' }}>
              I'll review your information and get back to you within 24 hours to schedule your first session.
            </p>
          </motion.div>
        </div>
      </ContactSection>
    );
  }

  return (
    <ContactSection id="contact">
      <div className="container">
        <motion.h2
          initial={{ y: 30 }}
          whileInView={{ y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          Get <span className="gradient-text">Started</span> Today
        </motion.h2>
        
        <motion.p 
          className="subtitle"
          initial={{ y: 20 }}
          whileInView={{ y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          viewport={{ once: true }}
        >
          Book a <strong>free 15-minute CS help call</strong> directly, or get started by telling me your course, assignment, deadline, and what you're stuck on. I'll reach out within 24 hours and let you know if I can help.
        </motion.p>

        <motion.div
          initial={{ y: 20 }}
          whileInView={{ y: 0 }}
          transition={{ duration: 0.6, delay: 0.15 }}
          viewport={{ once: true }}
        >
          <div className="contact-tabs" role="tablist" aria-label="Contact method">
            <button
              className={`contact-tab ${contactMode === 'booking' ? 'active' : ''}`}
              onClick={() => setContactMode('booking')}
              role="tab"
              aria-selected={contactMode === 'booking'}
              aria-controls="booking-panel"
              id="booking-tab"
            >
              <FaCalendarAlt aria-hidden="true" /> Book a Free 15-Min Call
            </button>
            <button
              className={`contact-tab ${contactMode === 'form' ? 'active' : ''}`}
              onClick={() => setContactMode('form')}
              role="tab"
              aria-selected={contactMode === 'form'}
              aria-controls="form-panel"
              id="form-tab"
            >
              <FaBookOpen aria-hidden="true" /> Get Started
            </button>
          </div>
        </motion.div>

        {contactMode === 'booking' ? (
          <motion.div
            className="calendly-container"
            initial={{ y: 30 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.4 }}
            role="tabpanel"
            id="booking-panel"
            aria-labelledby="booking-tab"
          >
              <div
              key={`calendly-${theme}`}
              className="calendly-inline-widget"
              data-url={calendlyUrl}
              style={{ minWidth: '320px', height: '700px' }}
            />
          </motion.div>
        ) : (
          <motion.div
            className="form-container"
            initial={{ y: 30 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.4 }}
            role="tabpanel"
            id="form-panel"
            aria-labelledby="form-tab"
          >
            {remainingSubmissions <= 1 && remainingSubmissions > 0 && (
              <div className="rate-limit-notice" role="alert">
                You have {remainingSubmissions} submission remaining this hour.
              </div>
            )}

            <form ref={formRef} onSubmit={handleSubmit} noValidate aria-label="Tutoring request form">
              <div className="form-grid">
              <div className="form-group full-width">
                <label htmlFor="firstName">Your Name <span aria-hidden="true">*</span><span className="sr-only">(required)</span></label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                  aria-required="true"
                  placeholder="Your full name"
                  autoComplete="name"
                  disabled={submissionState.isSubmitting}
                  aria-describedby={submitted && errors.firstName ? "firstName-error" : undefined}
                  aria-invalid={submitted && !!errors.firstName}
                />
                {submitted && errors.firstName && (
                  <span id="firstName-error" className="field-error" role="alert">
                    {errors.firstName}
                  </span>
                )}
              </div>

              <div className="form-group full-width">
                <label htmlFor="email">Email <span aria-hidden="true">*</span><span className="sr-only">(required)</span></label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  aria-required="true"
                  placeholder="your.email@example.com"
                  autoComplete="email"
                  disabled={submissionState.isSubmitting}
                  aria-describedby={submitted && errors.email ? "email-error" : undefined}
                  aria-invalid={submitted && !!errors.email}
                />
                {submitted && errors.email && (
                  <span id="email-error" className="field-error" role="alert">
                    {errors.email}
                  </span>
                )}
              </div>

              <div className="form-group full-width">
                <label htmlFor="goals">What are you stuck on? <span aria-hidden="true">*</span><span className="sr-only">(required)</span></label>
                <textarea
                  id="goals"
                  name="goals"
                  value={formData.goals}
                  onChange={handleInputChange}
                  required
                  aria-required="true"
                  placeholder="Tell me your course, assignment or topic, your deadline, and what you've already tried. The more detail, the better I can help."
                  rows="5"
                  disabled={submissionState.isSubmitting}
                  aria-describedby={submitted && errors.goals ? "goals-error" : undefined}
                  aria-invalid={submitted && !!errors.goals}
                />
                {submitted && errors.goals && (
                  <span id="goals-error" className="field-error" role="alert">
                    {errors.goals}
                  </span>
                )}
              </div>
            </div>

            {submitted && !isValid && (
              <div className="error-message" role="alert" aria-live="assertive">
                Please fill in all required fields correctly before submitting.
              </div>
            )}

            {submissionState.error && (
              <div className="error-message" role="alert" aria-live="assertive">
                {submissionState.error}
              </div>
            )}

            <button 
              type="submit" 
              className="submit-btn" 
              disabled={submissionState.isSubmitting || submissionState.isRateLimited}
              aria-describedby={submissionState.error ? "error-message" : undefined}
            >
              {submissionState.isSubmitting ? (
                <>
                  <motion.div
                    className="loading-spinner"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    aria-hidden="true"
                  />
                  <span>Submitting...</span>
                  <span className="sr-only">Please wait while your form is being submitted</span>
                </>
              ) : (
                <>
                  Get Started →
                </>
              )}
            </button>
          </form>
        </motion.div>
        )}
      </div>
    </ContactSection>
  );
}


const FooterContainer = styled.footer`
  padding: 3rem 2rem;
  background: ${({ theme }) => theme.primary};
  color: ${({ theme }) => theme.body};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, ${({ theme }) => theme.accent}, transparent);
  }

  .footer-content {
    max-width: 1200px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2rem;

    .footer-text {
      font-size: 0.875rem;
      opacity: 0.8;
      text-align: center;
      line-height: 1.6;
    }

    .footer-links {
      display: flex;
      gap: 2rem;

      a {
        color: ${({ theme }) => theme.body};
        opacity: 0.7;
        transition: all 0.3s ease;
        font-size: 1.25rem;

        &:hover {
          opacity: 1;
          transform: translateY(-2px);
        }
      }
    }
  }

  @media (max-width: 768px) {
    padding: 2rem 1rem;
  }
`;

function Footer() {
  return (
    <FooterContainer>
      <motion.div 
        className="footer-content"
        initial={{ y: 20 }}
        whileInView={{ y: 0 }}
        transition={{ duration: 0.5 }}
        viewport={{ once: true }}
      >
        <nav aria-label="Social media links">
          <div className="footer-links">
            <a href="https://www.linkedin.com/in/mabdelnaby1/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn profile (opens in new tab)">
              <FaLinkedin aria-hidden="true" />
            </a>
            <a href="https://github.com/MohamedHAbdelnaby" target="_blank" rel="noopener noreferrer" aria-label="GitHub profile (opens in new tab)">
              <FaGithub aria-hidden="true" />
            </a>
            <a href="https://x.com/iam_abdelnaby" target="_blank" rel="noopener noreferrer" aria-label="X profile (opens in new tab)">
              <FaXTwitter aria-hidden="true" />
            </a>
            <a href="mailto:tutoring@mohamedabdelnaby.com" aria-label="Send email to tutoring@mohamedabdelnaby.com">
              <FaEnvelope aria-hidden="true" />
            </a>
          </div>
        </nav>
        <div className="footer-text">
          © {new Date().getFullYear()} Mohamed Abdelnaby Computer Science Tutoring. All rights reserved.
        </div>
      </motion.div>
    </FooterContainer>
  );
}


const FAQSection = styled.section`
  padding: 5rem 2rem;
  background: ${({ theme }) => theme.sectionBg1};

  .container {
    max-width: 900px;
    margin: 0 auto;
  }

  h2 {
    font-size: clamp(2.5rem, 5vw, 3rem);
    text-align: center;
    margin-bottom: 1rem;
    color: ${({ theme }) => theme.text};

    span {
      background: ${({ theme }) => theme.gradientText};
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }
  }

  .intro {
    text-align: center;
    color: ${({ theme }) => theme.textLight};
    font-size: 1.05rem;
    margin-bottom: 3rem;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
  }

  .faq-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  details {
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: 12px;
    background: ${({ theme }) => theme.card};
    overflow: hidden;
    transition: box-shadow 0.2s, border-color 0.2s;

    &[open] {
      box-shadow: ${({ theme }) => theme.boxShadow};
      border-color: ${({ theme }) => theme.accent};
    }

    &:hover {
      border-color: ${({ theme }) => theme.accent};
    }
  }

  summary {
    padding: 1.25rem 1.5rem;
    cursor: pointer;
    font-weight: 600;
    font-size: 1.05rem;
    color: ${({ theme }) => theme.text};
    list-style: none;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;

    &::-webkit-details-marker { display: none; }
    &::after {
      content: '+';
      color: ${({ theme }) => theme.accent};
      font-size: 1.6rem;
      font-weight: 300;
      line-height: 1;
      flex-shrink: 0;
      transition: transform 0.2s;
    }

    &:focus-visible {
      outline: 2px solid ${({ theme }) => theme.accent};
      outline-offset: 2px;
      border-radius: 11px;
    }
  }

  details[open] summary::after {
    content: '−';
  }

  .faq-answer {
    padding: 0 1.5rem 1.4rem;
    color: ${({ theme }) => theme.textLight};
    font-size: 1rem;
    line-height: 1.7;
    margin: 0;
  }

  @media (prefers-reduced-motion: reduce) {
    details,
    summary::after {
      transition: none;
    }
  }

  @media (max-width: 600px) {
    padding: 3.5rem 1rem;
    summary { padding: 1rem 1.1rem; font-size: 1rem; }
    .faq-answer { padding: 0 1.1rem 1.1rem; font-size: 0.95rem; }
  }
`;

function FAQ() {
  const displayedFaqs = faqs.map((faq) => {
    const q = faq.question.toLowerCase();
    if (q.includes('cost')) {
      return {
        ...faq,
        answer: 'Standard sessions are $60 per hour. That includes one-on-one online support for programming, algorithms, AI/ML, cybersecurity, operating systems, computer architecture, projects, debugging, and exam preparation. For urgent deadlines, finals week, or after-hours support, send the details during the free 15-minute CS help call so I can confirm availability.'
      };
    }
    if (q.includes('level of students')) {
      return {
        ...faq,
        answer: 'I primarily work with ambitious high school students, college undergraduates, graduate students, and adult learners. I may also work with younger students when the fit is right, especially for advanced programming, competitions, or long-term enrichment.'
      };
    }
    return faq;
  });

  return (
    <FAQSection id="faq" aria-label="Frequently asked questions">
      <div className="container">
        <motion.h2
          initial={{ y: 30 }}
          whileInView={{ y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          Frequently Asked <span>Questions</span>
        </motion.h2>
        <p className="intro">
          Quick answers to the most common questions about tutoring sessions, pricing, and subjects covered.
        </p>
        <div className="faq-list">
          {displayedFaqs.map((faq, i) => (
            <details key={i}>
              <summary>{faq.question}</summary>
              <p className="faq-answer">{faq.answer}</p>
            </details>
          ))}
        </div>
      </div>
    </FAQSection>
  );
}


const ReferralSection = styled.section`
  padding: 4rem 2rem;
  background: ${({ theme }) => theme.sectionBg2};

  .container {
    max-width: 820px;
    margin: 0 auto;
  }

  .referral-card {
    display: flex;
    gap: 1.5rem;
    align-items: flex-start;
    padding: 2rem 2.25rem;
    background: ${({ theme }) => theme.card};
    border: 1px solid ${({ theme }) => theme.accent}40;
    border-radius: 18px;
    position: relative;
    overflow: hidden;
    box-shadow: ${({ theme }) => theme.boxShadow};

    &::before {
      content: '';
      position: absolute;
      top: -40%;
      right: -10%;
      width: 50%;
      height: 200%;
      background: radial-gradient(circle, ${({ theme }) => theme.accentSecondary}1A 0%, transparent 60%);
      pointer-events: none;
    }
  }

  .referral-icon {
    flex-shrink: 0;
    width: 52px;
    height: 52px;
    border-radius: 12px;
    background: ${({ theme }) => theme.gradient};
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 1.4rem;
    position: relative;
    z-index: 1;
  }

  .referral-content {
    flex: 1;
    position: relative;
    z-index: 1;
  }

  .referral-label {
    display: inline-block;
    font-size: 0.72rem;
    font-weight: 700;
    color: ${({ theme }) => theme.accent};
    text-transform: uppercase;
    letter-spacing: 0.12em;
    margin-bottom: 0.5rem;
  }

  .referral-card h2 {
    font-size: 1.35rem;
    font-weight: 700;
    color: ${({ theme }) => theme.text};
    margin: 0 0 0.6rem;
    letter-spacing: -0.01em;
  }

  .referral-card p {
    font-size: 0.98rem;
    line-height: 1.65;
    color: ${({ theme }) => theme.textLight};
    margin: 0;

    strong { color: ${({ theme }) => theme.text}; }
  }

  @media (max-width: 640px) {
    padding: 3rem 1.25rem;
    .referral-card {
      flex-direction: column;
      gap: 1rem;
      padding: 1.5rem;
    }
  }
`;

function Referral() {
  return (
    <ReferralSection aria-label="Refer a friend">
      <div className="container">
        <div className="referral-card">
          <div className="referral-icon" aria-hidden="true">
            <FiUsers />
          </div>
          <div className="referral-content">
            <span className="referral-label">Refer a Friend</span>
            <h2>Know another CS student who could use the help?</h2>
            <p>
              <strong>Refer a student who books 3 or more sessions</strong> and you'll get one
              discounted session or a free 30-minute review on a topic of your choice. Just have
              them mention your name during the free 15-minute call.
            </p>
          </div>
        </div>
      </div>
    </ReferralSection>
  );
}


// ============================================================================
// SUBJECT LANDING PAGE
// ============================================================================

const SubjectPageSection = styled.section`
  padding: 7rem 2rem 4.5rem;
  background: ${({ theme }) => theme.body};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -25%;
    right: -10%;
    width: 65vw;
    height: 65vw;
    max-width: 780px;
    max-height: 780px;
    border-radius: 50%;
    background: radial-gradient(circle, ${({ theme }) => theme.accent}1F 0%, transparent 60%);
    filter: blur(80px);
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -30%;
    left: -15%;
    width: 55vw;
    height: 55vw;
    max-width: 680px;
    max-height: 680px;
    border-radius: 50%;
    background: radial-gradient(circle, ${({ theme }) => theme.accentSecondary}14 0%, transparent 60%);
    filter: blur(80px);
    pointer-events: none;
  }

  .container {
    max-width: 1200px;
    margin: 0 auto;
    position: relative;
    z-index: 1;
    display: grid;
    grid-template-columns: minmax(0, 1.45fr) minmax(0, 1fr);
    gap: 4rem;
    align-items: center;
  }

  @media (max-width: 960px) {
    .container {
      grid-template-columns: 1fr;
      gap: 2.75rem;
    }
  }

  .hero-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.4rem 0.95rem;
    background: ${({ theme }) => theme.accent}14;
    color: ${({ theme }) => theme.accent};
    border: 1px solid ${({ theme }) => theme.accent}33;
    border-radius: 999px;
    font-size: 0.76rem;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    margin: 0 0 1.5rem;
  }

  h1 {
    font-size: clamp(2.1rem, 4.8vw, 3.1rem);
    font-weight: 800;
    letter-spacing: -0.025em;
    line-height: 1.1;
    color: ${({ theme }) => theme.text};
    margin: 0 0 1rem;
  }

  .subject-subtitle {
    font-size: 1.15rem;
    color: ${({ theme }) => theme.textLight};
    font-weight: 500;
    margin: 0 0 1.5rem;
    line-height: 1.5;
  }

  .subject-intro {
    font-size: 1.05rem;
    line-height: 1.7;
    color: ${({ theme }) => theme.text};
    margin: 0 0 2rem;
    max-width: 620px;
  }

  .subject-audience {
    font-size: 0.9rem;
    color: ${({ theme }) => theme.textLight};
    margin: 1.5rem 0 0;
    padding: 0.85rem 1.1rem;
    background: ${({ theme }) => theme.card};
    border: 1px solid ${({ theme }) => theme.borderLight};
    border-radius: 10px;
    border-left: 3px solid ${({ theme }) => theme.accent};
  }

  .subject-cta-row {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
    align-items: center;

    a {
      display: inline-flex;
      align-items: center;
      gap: 0.6rem;
      padding: 0.95rem 1.85rem;
      border-radius: 12px;
      font-weight: 600;
      font-size: 1rem;
      transition: transform 0.3s ease, box-shadow 0.3s ease;
      text-decoration: none;

      &:focus-visible {
        outline: 2px solid ${({ theme }) => theme.accent};
        outline-offset: 3px;
      }
      &:hover { transform: translateY(-2px); }
      @media (prefers-reduced-motion: reduce) {
        &:hover { transform: none; }
      }
    }

    .primary {
      background: ${({ theme }) => theme.gradient};
      color: white;
      box-shadow: 0 4px 18px -2px ${({ theme }) => theme.accent}4D;
      &:hover { box-shadow: 0 12px 32px -4px ${({ theme }) => theme.accent}80; }
    }
  }

  .credentials-card {
    background: ${({ theme }) => theme.card};
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: 20px;
    padding: 1.85rem;
    box-shadow: ${({ theme }) => theme.boxShadow};
    position: relative;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 4px;
      background: ${({ theme }) => theme.gradient};
    }

    .cred-header {
      display: flex;
      gap: 1rem;
      align-items: center;
      margin-bottom: 1.5rem;
    }

    .cred-photo {
      width: 68px;
      height: 68px;
      border-radius: 50%;
      object-fit: cover;
      border: 2px solid ${({ theme }) => theme.accent}66;
      flex-shrink: 0;
    }

    .cred-name-block {
      h3 {
        font-size: 1.1rem;
        font-weight: 800;
        color: ${({ theme }) => theme.text};
        margin: 0 0 0.2rem;
        letter-spacing: -0.01em;
      }
      .cred-title {
        font-size: 0.85rem;
        color: ${({ theme }) => theme.textLight};
        font-weight: 500;
      }
    }

    .cred-list {
      list-style: none;
      padding: 0;
      margin: 0 0 1.5rem;
      display: flex;
      flex-direction: column;
      gap: 0.55rem;
    }

    .cred-item {
      display: flex;
      gap: 0.6rem;
      align-items: center;
      font-size: 0.92rem;
      color: ${({ theme }) => theme.text};

      svg {
        color: ${({ theme }) => theme.accent};
        font-size: 0.8rem;
        flex-shrink: 0;
      }
    }

    .cred-stats {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 0.75rem;
      padding: 1rem 0;
      border-top: 1px solid ${({ theme }) => theme.borderLight};
      border-bottom: 1px solid ${({ theme }) => theme.borderLight};
      margin-bottom: 1.25rem;

      .stat {
        text-align: center;
      }
      .num {
        display: block;
        font-size: 1.35rem;
        font-weight: 800;
        background: ${({ theme }) => theme.gradient};
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
        letter-spacing: -0.02em;
      }
      .lbl {
        display: block;
        font-size: 0.7rem;
        color: ${({ theme }) => theme.textLight};
        font-weight: 600;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        margin-top: 0.15rem;
      }
    }

    .cred-price {
      text-align: center;

      .price-num {
        font-size: 1.85rem;
        font-weight: 800;
        background: ${({ theme }) => theme.gradient};
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
        letter-spacing: -0.02em;
      }
      .price-unit {
        font-size: 0.95rem;
        color: ${({ theme }) => theme.textLight};
        font-weight: 600;
        margin-left: 0.15rem;
      }
      .price-note {
        margin-top: 0.4rem;
        font-size: 0.82rem;
        color: ${({ theme }) => theme.textLight};
      }
    }
  }

  @media (max-width: 600px) {
    padding: 5.75rem 1.25rem 3rem;

    .credentials-card { padding: 1.5rem; }
  }
`;

const SubjectHowItWorksSection = styled.section`
  padding: 4rem 2rem;
  background: ${({ theme }) => theme.sectionBg2};

  .container {
    max-width: 1100px;
    margin: 0 auto;
  }

  h2 {
    font-size: clamp(1.55rem, 3vw, 1.95rem);
    font-weight: 800;
    letter-spacing: -0.02em;
    margin: 0 0 0.5rem;
    color: ${({ theme }) => theme.text};
    text-align: center;
  }

  .section-tagline {
    text-align: center;
    color: ${({ theme }) => theme.textLight};
    font-size: 1rem;
    margin: 0 0 2.5rem;
  }

  .steps-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 1.25rem;
  }

  .step-card {
    background: ${({ theme }) => theme.card};
    border: 1px solid ${({ theme }) => theme.borderLight};
    border-radius: 16px;
    padding: 1.75rem 1.5rem;
    position: relative;
    transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;

    &:hover {
      transform: translateY(-4px);
      box-shadow: ${({ theme }) => theme.boxShadowHover};
      border-color: ${({ theme }) => theme.accent}66;
    }
    @media (prefers-reduced-motion: reduce) {
      &:hover { transform: none; }
    }
  }

  .step-num {
    position: absolute;
    top: 1rem;
    right: 1.4rem;
    font-size: 2.75rem;
    font-weight: 800;
    line-height: 1;
    background: ${({ theme }) => theme.gradient};
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    opacity: 0.55;
    letter-spacing: -0.04em;
  }

  .step-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: ${({ theme }) => theme.accent}1A;
    color: ${({ theme }) => theme.accent};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
    margin-bottom: 1rem;
  }

  .step-card h3 {
    font-size: 1.1rem;
    font-weight: 700;
    color: ${({ theme }) => theme.text};
    margin: 0 0 0.5rem;
    letter-spacing: -0.01em;
  }

  .step-card p {
    font-size: 0.95rem;
    line-height: 1.55;
    color: ${({ theme }) => theme.textLight};
    margin: 0;
  }

  @media (max-width: 600px) {
    padding: 3rem 1.25rem;
  }
`;

const SubjectOutcomesSection = styled.section`
  padding: 4rem 2rem;
  background: ${({ theme }) => theme.sectionBg1};

  .container {
    max-width: 1100px;
    margin: 0 auto;
  }

  h2 {
    font-size: clamp(1.55rem, 3vw, 1.95rem);
    font-weight: 800;
    letter-spacing: -0.02em;
    margin: 0 0 0.5rem;
    color: ${({ theme }) => theme.text};
    text-align: center;
  }

  .section-tagline {
    text-align: center;
    color: ${({ theme }) => theme.textLight};
    font-size: 1rem;
    margin: 0 0 2.5rem;
  }

  .outcomes-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1rem;
  }

  .outcome-card {
    background: ${({ theme }) => theme.card};
    border: 1px solid ${({ theme }) => theme.borderLight};
    border-radius: 14px;
    padding: 1.25rem;
    display: flex;
    gap: 0.85rem;
    align-items: flex-start;
    transition: border-color 0.2s ease;

    &:hover {
      border-color: ${({ theme }) => theme.accent}66;
    }
  }

  .outcome-check {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: ${({ theme }) => theme.gradient};
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.72rem;
    flex-shrink: 0;
    margin-top: 0.15rem;
  }

  .outcome-text {
    font-size: 0.98rem;
    line-height: 1.5;
    color: ${({ theme }) => theme.text};
  }

  @media (max-width: 600px) {
    padding: 3rem 1.25rem;
  }
`;

const SubjectRelatedSection = styled.section`
  padding: 4rem 2rem;
  background: ${({ theme }) => theme.sectionBg2};

  .container {
    max-width: 1100px;
    margin: 0 auto;
  }

  h2 {
    font-size: clamp(1.55rem, 3vw, 1.95rem);
    font-weight: 800;
    letter-spacing: -0.02em;
    margin: 0 0 0.5rem;
    color: ${({ theme }) => theme.text};
    text-align: center;
  }

  .section-tagline {
    text-align: center;
    color: ${({ theme }) => theme.textLight};
    font-size: 1rem;
    margin: 0 0 2.5rem;
  }

  .related-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1.25rem;
  }

  .related-card {
    background: ${({ theme }) => theme.card};
    border: 1px solid ${({ theme }) => theme.borderLight};
    border-radius: 16px;
    padding: 1.5rem;
    text-decoration: none;
    display: flex;
    flex-direction: column;
    gap: 0.55rem;
    transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease;

    &:hover {
      transform: translateY(-4px);
      box-shadow: ${({ theme }) => theme.boxShadowHover};
      border-color: ${({ theme }) => theme.accent}66;
    }
    @media (prefers-reduced-motion: reduce) {
      &:hover { transform: none; }
    }

    &:focus-visible {
      outline: 2px solid ${({ theme }) => theme.accent};
      outline-offset: 3px;
    }
  }

  .related-eyebrow {
    font-size: 0.7rem;
    color: ${({ theme }) => theme.accent};
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }

  .related-title {
    font-size: 1.1rem;
    font-weight: 700;
    color: ${({ theme }) => theme.text};
    letter-spacing: -0.01em;
    margin: 0;
  }

  .related-desc {
    font-size: 0.92rem;
    line-height: 1.55;
    color: ${({ theme }) => theme.textLight};
    margin: 0;
  }

  .related-link {
    margin-top: auto;
    padding-top: 0.5rem;
    color: ${({ theme }) => theme.accent};
    font-size: 0.9rem;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
  }

  @media (max-width: 600px) {
    padding: 3rem 1.25rem;
  }
`;

const SubjectContentSection = styled.section`
  padding: 3.5rem 2rem;
  background: ${({ theme }) => theme.sectionBg1};

  .container {
    max-width: 820px;
    margin: 0 auto;
  }

  h2 {
    font-size: clamp(1.5rem, 3vw, 1.85rem);
    font-weight: 800;
    letter-spacing: -0.02em;
    margin: 0 0 1.75rem;
    color: ${({ theme }) => theme.text};
  }

  .subsection-heading {
    font-size: 0.95rem;
    font-weight: 700;
    color: ${({ theme }) => theme.textLight};
    text-transform: uppercase;
    letter-spacing: 0.06em;
    margin: 2rem 0 1rem;

    &:first-of-type { margin-top: 0; }
  }

  .pain-points {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.65rem;
  }

  .pain-point {
    padding: 0.95rem 1.2rem;
    background: ${({ theme }) => theme.card};
    border: 1px solid ${({ theme }) => theme.borderLight};
    border-radius: 10px;
    color: ${({ theme }) => theme.text};
    font-size: 0.98rem;
    line-height: 1.55;
  }

  .topics-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 0.6rem;
  }

  .topic-pill {
    padding: 0.65rem 0.9rem;
    background: ${({ theme }) => theme.card};
    border: 1px solid ${({ theme }) => theme.borderLight};
    border-radius: 8px;
    color: ${({ theme }) => theme.textLight};
    font-size: 0.92rem;
    line-height: 1.4;
    transition: border-color 0.2s ease, color 0.2s ease;

    &:hover {
      border-color: ${({ theme }) => theme.accent};
      color: ${({ theme }) => theme.text};
    }
  }

  @media (max-width: 600px) {
    padding: 2.75rem 1.25rem;
  }
`;

const SubjectTestimonialsSection = styled.section`
  padding: 3.5rem 2rem;
  background: ${({ theme }) => theme.sectionBg2};

  .container {
    max-width: 820px;
    margin: 0 auto;
  }

  h2 {
    font-size: clamp(1.5rem, 3vw, 1.85rem);
    font-weight: 800;
    letter-spacing: -0.02em;
    margin: 0 0 1.75rem;
    color: ${({ theme }) => theme.text};
  }

  .reviews-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 1.25rem;
  }

  .review-card {
    background: ${({ theme }) => theme.card};
    border: 1px solid ${({ theme }) => theme.borderLight};
    border-radius: 16px;
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 0.6rem;

    .stars {
      display: flex;
      gap: 0.15rem;
      svg { color: #ffa726; font-size: 0.9rem; }
    }

    h3 {
      font-size: 1rem;
      font-weight: 700;
      color: ${({ theme }) => theme.text};
      margin: 0.25rem 0;
    }

    p.quote {
      font-size: 0.9rem;
      line-height: 1.6;
      color: ${({ theme }) => theme.textLight};
      margin: 0 0 0.5rem;
    }

    .student {
      font-size: 0.85rem;
      font-weight: 600;
      color: ${({ theme }) => theme.accent};
      padding-top: 0.5rem;
      border-top: 1px solid ${({ theme }) => theme.borderLight};
    }
  }

  @media (max-width: 600px) {
    padding: 3rem 1.25rem;
  }
`;

const SubjectCTASection = styled.section`
  padding: 3.5rem 2rem 4.5rem;
  background: ${({ theme }) => theme.body};
  text-align: center;

  .container {
    max-width: 680px;
    margin: 0 auto;
  }

  p {
    color: ${({ theme }) => theme.text};
    font-size: 1.05rem;
    margin: 0 0 1.5rem;
    line-height: 1.65;
  }

  .cta-buttons {
    display: flex;
    justify-content: center;
    gap: 1rem;
    flex-wrap: wrap;

    a {
      display: inline-flex;
      align-items: center;
      gap: 0.6rem;
      padding: 0.95rem 2rem;
      border-radius: 12px;
      font-weight: 600;
      font-size: 1rem;
      transition: transform 0.3s ease, box-shadow 0.3s ease;
      text-decoration: none;

      &:focus-visible {
        outline: 2px solid ${({ theme }) => theme.accent};
        outline-offset: 3px;
      }
      &:hover { transform: translateY(-2px); }
      @media (prefers-reduced-motion: reduce) {
        &:hover { transform: none; }
      }
    }

    .primary {
      background: ${({ theme }) => theme.gradient};
      color: white;
      box-shadow: 0 4px 18px -2px ${({ theme }) => theme.accent}40;
      &:hover { box-shadow: 0 10px 30px -4px ${({ theme }) => theme.accent}66; }
    }
    .secondary {
      background: transparent;
      color: ${({ theme }) => theme.text};
      border: 1.5px solid ${({ theme }) => theme.border};
      &:hover { border-color: ${({ theme }) => theme.accent}; }
    }
  }

  @media (max-width: 600px) {
    padding: 3.5rem 1.25rem;
  }
`;

function SubjectLandingPage({ subject, toggleTheme, theme }) {
  useEffect(() => {
    const SITE_ORIGIN = 'https://tutor.mohamedabdelnaby.com';
    const pageUrl = `${SITE_ORIGIN}/${subject.slug}`;
    // Subject pages share the homepage OG image (Mohamed.jpg) for now.
    // When per-subject 1200x630 PNGs are generated, swap to `/og-images/${subject.slug}.png`.
    const ogImageUrl = `${SITE_ORIGIN}/mohamed.jpg`;

    const previousTitle = document.title;
    document.title = subject.pageTitle;

    // Helper: upsert a meta tag by attribute key. Returns a cleanup that restores
    // the previous content (or removes the element if it didn't exist).
    const upsertMeta = (attrName, attrValue, content) => {
      let el = document.querySelector(`meta[${attrName}="${attrValue}"]`);
      const created = !el;
      let previousContent = null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attrName, attrValue);
        document.head.appendChild(el);
      } else {
        previousContent = el.getAttribute('content');
      }
      el.setAttribute('content', content);
      return () => {
        if (created) {
          el.remove();
        } else if (previousContent !== null) {
          el.setAttribute('content', previousContent);
        }
      };
    };

    // Helper: upsert a link tag by rel
    const upsertLink = (rel, href) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      const created = !el;
      let previousHref = null;
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        document.head.appendChild(el);
      } else {
        previousHref = el.getAttribute('href');
      }
      el.setAttribute('href', href);
      return () => {
        if (created) {
          el.remove();
        } else if (previousHref !== null) {
          el.setAttribute('href', previousHref);
        }
      };
    };

    const cleanups = [
      upsertMeta('name', 'description', subject.metaDescription),
      upsertLink('canonical', pageUrl),
      // Open Graph
      upsertMeta('property', 'og:type', 'website'),
      upsertMeta('property', 'og:locale', 'en_US'),
      upsertMeta('property', 'og:title', subject.pageTitle),
      upsertMeta('property', 'og:description', subject.metaDescription),
      upsertMeta('property', 'og:url', pageUrl),
      upsertMeta('property', 'og:image', ogImageUrl),
      upsertMeta('property', 'og:image:alt', `${subject.heroHeadline}, Mohamed Abdelnaby tutoring`),
      upsertMeta('property', 'og:image:width', '1258'),
      upsertMeta('property', 'og:image:height', '1233'),
      upsertMeta('property', 'og:image:type', 'image/jpeg'),
      upsertMeta('property', 'og:site_name', 'Mohamed Abdelnaby Computer Science Tutoring'),
      // Twitter (summary, not summary_large_image, since the image is square)
      upsertMeta('name', 'twitter:card', 'summary'),
      upsertMeta('name', 'twitter:creator', '@iam_abdelnaby'),
      upsertMeta('name', 'twitter:title', subject.pageTitle),
      upsertMeta('name', 'twitter:description', subject.metaDescription),
      upsertMeta('name', 'twitter:image', ogImageUrl),
      upsertMeta('name', 'twitter:image:alt', `${subject.heroHeadline}, Mohamed Abdelnaby tutoring`)
    ];

    // Inject per-page Service JSON-LD that references the homepage's
    // LocalBusiness as the provider via @id (clean linked-data graph).
    const serviceSchema = {
      "@context": "https://schema.org",
      "@type": "Service",
      "name": subject.heroHeadline,
      "serviceType": `Online ${subject.heroHeadline}`,
      "description": subject.metaDescription,
      "provider": { "@id": `${SITE_ORIGIN}/#business` },
      "areaServed": { "@type": "Place", "name": "Worldwide (Online)" },
      "audience": {
        "@type": "EducationalAudience",
        "educationalRole": "student"
      },
      "url": pageUrl,
      "image": ogImageUrl,
      "offers": {
        "@type": "Offer",
        "priceCurrency": "USD",
        "price": "60",
        "priceSpecification": {
          "@type": "UnitPriceSpecification",
          "price": "60",
          "priceCurrency": "USD",
          "unitText": "HOUR"
        },
        "availability": "https://schema.org/InStock",
        "url": `${SITE_ORIGIN}/#contact`
      },
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": `${subject.navTitle} topics`,
        "itemListElement": subject.topics.map((topic, i) => ({
          "@type": "Offer",
          "position": i + 1,
          "itemOffered": {
            "@type": "Service",
            "name": topic
          }
        }))
      }
    };
    const schemaScript = document.createElement('script');
    schemaScript.setAttribute('type', 'application/ld+json');
    schemaScript.setAttribute('data-subject-schema', subject.slug);
    schemaScript.textContent = JSON.stringify(serviceSchema);
    document.head.appendChild(schemaScript);

    window.scrollTo(0, 0);

    return () => {
      document.title = previousTitle;
      cleanups.forEach(fn => fn && fn());
      schemaScript.remove();
    };
  }, [subject]);

  const relevantTestimonials = TESTIMONIALS
    .filter(t => t.category === subject.testimonialCategory)
    .slice(0, 3);

  const relatedSubjects = (subject.relatedSlugs || [])
    .map(slug => subjects.find(s => s.slug === slug))
    .filter(Boolean)
    .slice(0, 3);

  const outcomes = [
    "A clear plan to finish your assignment, project, or exam prep.",
    "Confidence in the concepts your course is actually testing.",
    "Code you understand line by line, not just code that compiles.",
    "A faster way to debug your own work when I'm not around.",
    "Notes and worked examples you can come back to before the exam.",
    "Honest feedback on what to focus on, and what to skip."
  ];

  return (
    <>
      <HomeSEO />
      <a href="#main-content" className="skip-nav">Skip to main content</a>
      <Header toggleTheme={toggleTheme} theme={theme} />
      <main id="main-content" tabIndex="-1">
        <SubjectPageSection>
          <div className="container">
            <div>
              <span className="hero-eyebrow">
                <FiZap aria-hidden="true" /> 1-on-1 CS Tutoring
              </span>
              <h1>{subject.heroHeadline}</h1>
              <p className="subject-subtitle">{subject.heroSubtitle}</p>
              <p className="subject-intro">{subject.intro}</p>
              <div className="subject-cta-row">
                <a href="/#contact" className="primary">
                  {subject.ctaText} <FaArrowRight aria-hidden="true" />
                </a>
              </div>
              <p className="subject-audience">{subject.audienceLine}</p>
            </div>

            <aside className="credentials-card" aria-label="About the tutor">
              <div className="cred-header">
                <img
                  src={MohamedImage}
                  alt="Mohamed Abdelnaby"
                  className="cred-photo"
                  loading="lazy"
                />
                <div className="cred-name-block">
                  <h3>Mohamed Abdelnaby</h3>
                  <div className="cred-title">CS PhD, Virginia Tech</div>
                </div>
              </div>
              <ul className="cred-list">
                <li className="cred-item">
                  <FaCheck aria-hidden="true" /> Teaching Assistant for undergraduate and graduate CS courses
                </li>
                <li className="cred-item">
                  <FaCheck aria-hidden="true" /> Published ML researcher
                </li>
                <li className="cred-item">
                  <FaCheck aria-hidden="true" /> 4.0 graduate GPA
                </li>
                <li className="cred-item">
                  <FaCheck aria-hidden="true" /> Replies within 24 hours
                </li>
              </ul>
              <div className="cred-stats">
                <div className="stat">
                  <span className="num">1,800+</span>
                  <span className="lbl">Hours taught</span>
                </div>
                <div className="stat">
                  <span className="num">250+</span>
                  <span className="lbl">5-star ratings</span>
                </div>
              </div>
              <div className="cred-price">
                <span className="price-num">$60</span>
                <span className="price-unit">/ hour</span>
                <div className="price-note">Free 15-min call to see if we're a fit</div>
              </div>
            </aside>
          </div>
        </SubjectPageSection>

        <SubjectContentSection>
          <div className="container">
            <h2>What I help with</h2>

            <h3 className="subsection-heading">Common things students bring to sessions</h3>
            <ul className="pain-points">
              {subject.painPoints.map((point, i) => (
                <li key={i} className="pain-point">{point}</li>
              ))}
            </ul>

            <h3 className="subsection-heading">Topics covered</h3>
            <div className="topics-grid">
              {subject.topics.map((topic, i) => (
                <div key={i} className="topic-pill">{topic}</div>
              ))}
            </div>
          </div>
        </SubjectContentSection>

        <SubjectHowItWorksSection>
          <div className="container">
            <h2>How sessions work</h2>
            <p className="section-tagline">Three steps from stuck to unstuck.</p>
            <div className="steps-grid">
              <div className="step-card">
                <span className="step-num" aria-hidden="true">01</span>
                <div className="step-icon"><FiTarget aria-hidden="true" /></div>
                <h3>Free 15-minute call</h3>
                <p>Tell me your course, the assignment, your deadline, and what you are stuck on. I will let you know if I can help.</p>
              </div>
              <div className="step-card">
                <span className="step-num" aria-hidden="true">02</span>
                <div className="step-icon"><FaCalendarAlt aria-hidden="true" /></div>
                <h3>Schedule your session</h3>
                <p>We pick times that work around your week. Single sessions, weekly slots, or short bursts before exams, all fine.</p>
              </div>
              <div className="step-card">
                <span className="step-num" aria-hidden="true">03</span>
                <div className="step-icon"><FiZap aria-hidden="true" /></div>
                <h3>Walk in stuck, walk out unstuck</h3>
                <p>We work through your actual code, problem set, or concepts together until it clicks. You leave with notes you can use.</p>
              </div>
            </div>
          </div>
        </SubjectHowItWorksSection>

        <SubjectOutcomesSection>
          <div className="container">
            <h2>What you'll walk away with</h2>
            <p className="section-tagline">Real outcomes, not just an hour spent.</p>
            <div className="outcomes-grid">
              {outcomes.map((text, i) => (
                <div key={i} className="outcome-card">
                  <span className="outcome-check" aria-hidden="true"><FaCheck /></span>
                  <span className="outcome-text">{text}</span>
                </div>
              ))}
            </div>
          </div>
        </SubjectOutcomesSection>

        {relevantTestimonials.length > 0 && (
          <SubjectTestimonialsSection>
            <div className="container">
              <h2>What students have said</h2>
              <div className="reviews-grid">
                {relevantTestimonials.map((t, i) => (
                  <div key={i} className="review-card">
                    <div className="stars" aria-label={`${t.rating} out of 5 stars`}>
                      {[...Array(t.rating)].map((_, idx) => <FaStar key={idx} aria-hidden="true" />)}
                    </div>
                    <h3>{t.title}</h3>
                    <p className="quote">"{t.text}"</p>
                    <div className="student">{t.student}, {t.lessons} lesson{t.lessons !== 1 ? 's' : ''}</div>
                  </div>
                ))}
              </div>
            </div>
          </SubjectTestimonialsSection>
        )}

        {relatedSubjects.length > 0 && (
          <SubjectRelatedSection>
            <div className="container">
              <h2>Related subjects</h2>
              <p className="section-tagline">If this is not quite the right fit, here are the closest matches.</p>
              <div className="related-grid">
                {relatedSubjects.map((s) => (
                  <a key={s.slug} href={`/${s.slug}`} className="related-card">
                    <span className="related-eyebrow">Tutoring</span>
                    <h3 className="related-title">{s.navTitle}</h3>
                    <p className="related-desc">{s.heroSubtitle}</p>
                    <span className="related-link">
                      View page <FaArrowRight aria-hidden="true" />
                    </span>
                  </a>
                ))}
              </div>
            </div>
          </SubjectRelatedSection>
        )}

        <SubjectCTASection>
          <div className="container">
            <p>
              Ready to get unstuck? Start with a free 15-minute call. Tell me
              your course, the assignment, your deadline, and what is in the
              way. If I can help, we will book a real session.
            </p>
            <div className="cta-buttons">
              <a href="/#contact" className="primary">
                {subject.ctaText} <FaArrowRight aria-hidden="true" />
              </a>
            </div>
          </div>
        </SubjectCTASection>
      </main>
      <Footer />
    </>
  );
}


// ============================================================================
// HOME PAGE & APP ROUTING
// ============================================================================


function HomeSEO() {
  useEffect(() => {
    const SITE_ORIGIN = 'https://tutor.mohamedabdelnaby.com';
    const pageTitle = 'Advanced CS Tutor | AI, ML, Algorithms, Cybersecurity | Mohamed Abdelnaby';
    const pageDescription = 'Advanced computer science tutoring for hard courses, projects, and exams. Work with Mohamed Abdelnaby, a CS PhD student, Graduate Teaching Assistant, and published ML researcher with 1,800+ hours taught and 250+ five-star ratings.';

    const previousTitle = document.title;
    document.title = pageTitle;

    const upsertMeta = (attrName, attrValue, content) => {
      let el = document.querySelector(`meta[${attrName}="${attrValue}"]`);
      const created = !el;
      const previousContent = el?.getAttribute('content');
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attrName, attrValue);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
      return () => {
        if (created) el.remove();
        else if (previousContent !== null && previousContent !== undefined) el.setAttribute('content', previousContent);
      };
    };

    const upsertLink = (rel, href) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      const created = !el;
      const previousHref = el?.getAttribute('href');
      if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        document.head.appendChild(el);
      }
      el.setAttribute('href', href);
      return () => {
        if (created) el.remove();
        else if (previousHref !== null && previousHref !== undefined) el.setAttribute('href', previousHref);
      };
    };

    const cleanups = [
      upsertMeta('name', 'description', pageDescription),
      upsertLink('canonical', SITE_ORIGIN),
      upsertMeta('property', 'og:type', 'website'),
      upsertMeta('property', 'og:locale', 'en_US'),
      upsertMeta('property', 'og:title', pageTitle),
      upsertMeta('property', 'og:description', pageDescription),
      upsertMeta('property', 'og:url', SITE_ORIGIN),
      upsertMeta('property', 'og:site_name', 'Mohamed Abdelnaby Computer Science Tutoring'),
      upsertMeta('name', 'twitter:card', 'summary_large_image'),
      upsertMeta('name', 'twitter:title', pageTitle),
      upsertMeta('name', 'twitter:description', pageDescription),
    ];

    return () => {
      document.title = previousTitle;
      cleanups.forEach(cleanup => cleanup());
    };
  }, []);

  return null;
}

function HomePage({ toggleTheme, theme }) {
  return (
    <>
      <HomeSEO />
      <a href="#main-content" className="skip-nav">Skip to main content</a>
      <Header toggleTheme={toggleTheme} theme={theme} />
      <main id="main-content" tabIndex="-1">
        <Hero />
        <Testimonials />
        <TACredibility />
        <CommonReasons />
        <Subjects />
        <About />
        <WhoIHelp />
        <FAQ />
        <Contact theme={theme} />
        <Referral />
      </main>
      <Footer />
    </>
  );
}

function App() {
  const [theme, setTheme] = useState('dark');
  const currentTheme = theme === 'light' ? lightTheme : darkTheme;

  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
  };

  return (
    <ThemeProvider theme={currentTheme}>
      <GlobalStyles />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage toggleTheme={toggleTheme} theme={theme} />} />
          {subjects.map((subject) => (
            <Route
              key={subject.slug}
              path={`/${subject.slug}`}
              element={<SubjectLandingPage subject={subject} toggleTheme={toggleTheme} theme={theme} />}
            />
          ))}
          {/* Any unknown path falls back to the home page */}
          <Route path="*" element={<HomePage toggleTheme={toggleTheme} theme={theme} />} />
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;